import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, Navigate, useParams, useSearchParams } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import ArabicLoader from '../components/ArabicLoader';
import { analysisPost, userWriteHeaders, userReadHeaders } from '../lib/api';
import { supabase } from '../lib/supabase';
import { lsGet, lsSet, guestSaveProgress } from '../lib/storage';
import { trackEvent } from '../lib/analytics';
import { captureException } from '../lib/monitoring';
import HelpSheet from '../components/HelpSheet';
import { generateStemCandidates } from '../lib/arabic-morphology';
import SpotlightTour from '../components/SpotlightTour';
import BugReportLink from '../components/BugReportLink';
import AskAiSection from '../components/AskAiSection.jsx';
import { useVerseDepth } from '../hooks/use-verse-depth.js';
import AnalysisModal from '../components/AnalysisModal';
import TopUpSheet from '../components/TopUpSheet';
import { useWallet } from '../hooks/use-wallet';

const API = window.location.origin;

const READ_HELP = [
  { icon: '✋', label: 'Tap a word', desc: 'Tap any Arabic word to open a popup with its meaning, root letters, and grammar role. Hit ＋ Save Word to add it to your flashcard deck.' },
  { icon: '📖', label: 'Poem Context', desc: 'Tap the collapsible banner at the top to read poet background, contemporaries, theme, and literary interpretations.' },
  { icon: '← →', label: 'Navigate bayts', desc: 'Use the arrows to move between verses. The dots show which bayt you are on — tap a dot to jump directly.' },
  { icon: '🌐', label: 'Translation', desc: 'Tap "Show translation" below the verse to reveal an English translation of the bayt.' },
];

const STUDY_HELP = [
  { icon: '📚', label: 'Six tools per bayt', desc: 'Tap any row to expand: Plain Reading, Literary Devices, Vocabulary, Root Explorer, Grammar Layer (colour-coded إعراب), and a Cloze fill-the-gap exercise.' },
  { icon: '✏️', label: 'Cloze Exercise', desc: 'Fill in the missing words from memory. Tap a word chip at the bottom, then tap a blank in the verse to place it.' },
  { icon: '🌱', label: 'Root Explorer', desc: 'See how each word shares a three-letter Arabic root with related words — a core tool for building vocabulary.' },
  { icon: '🏷', label: 'Grammar Layer', desc: 'Colour-coded إعراب: blue = noun, red = verb, yellow = particle, green = adjective. Tap a word to see its role.' },
];

const REFERENCE_HELP = [
  { icon: '📋', label: 'Full poem', desc: 'All bayts displayed at once so you can read the poem as a whole, without navigating verse by verse.' },
  { icon: '💾', label: 'Saved words', desc: 'Every word you saved from Study appears here. Tap "View in Flashcards" to start a review session from this poem.' },
  { icon: '📜', label: 'Poem details', desc: 'Poet, era, metre, and topic — all metadata for the poem in one place.' },
];

const stripMd = (t) => (t || '').replace(/\*\*/g, '').replace(/^\s*[-–•]\s*/, '').trim();
const stripArabicPrefix = (t, prefix) =>
  (t || '').replace(new RegExp(`^${prefix}[:\\s]*`, 'u'), '').trim();

// ── Study tab data ────────────────────────────────────────────────────────────
function stripHarakat(str) {
  if (!str) return '';
  return str.replace(/[ً-ٰٟ]/g, '');
}


// Strip prefix letters and the definite article for robust word matching.
// بِالطَلَبِ → طلب  |  الطَلَبُ → طلب  |  وَالبُهتانَ → بهتان
function normalizeArabicWord(w) {
  let s = stripHarakat(w);
  if (/^[وفبلك]ال/.test(s)) s = s.slice(1); // strip one prefix letter before ال
  if (s.startsWith('ال')) s = s.slice(2);    // strip standalone ال
  return s;
}

// Three-tier vocab lookup:
//   1. Exact normalised match             كَتَبَ  → كتب  === كتب
//   2. startsWith (pronoun suffix)        كَتَبتُكِ → كتبتك startsWith كتب
//   3. Stem candidates (مضارع prefix / conjugation suffix stripped)
//      يَكتُبُونَ → ['يكتبون','كتبون','يكتب','كتب'] — one of these matches كتب
function findVocabEntry(vocabulary, tappedAr) {
  const vocab = Array.isArray(vocabulary) ? vocabulary : [];
  const norm = normalizeArabicWord(tappedAr);

  const exact = vocab.find(v => v?.wordAr && normalizeArabicWord(v.wordAr) === norm);
  if (exact) return exact;

  const sw = vocab.find(v => v?.wordAr && norm.startsWith(normalizeArabicWord(v.wordAr)) && normalizeArabicWord(v.wordAr).length >= 3);
  if (sw) return sw;

  const candidates = generateStemCandidates(tappedAr);
  for (const c of candidates) {
    if (c === norm) continue;
    const m = vocab.find(v => v?.wordAr && normalizeArabicWord(v.wordAr) === c);
    if (m) return m;
    const ms = vocab.find(v => v?.wordAr && c.startsWith(normalizeArabicWord(v.wordAr)) && normalizeArabicWord(v.wordAr).length >= 3);
    if (ms) return ms;
  }

  return null;
}

function splitVerseWords(textAr) {
  const parts = textAr.split(' / ');
  const result = [];
  parts.forEach((part, pi) => {
    const words = part.trim().split(/\s+/).filter(Boolean);
    words.forEach((w, wi) => {
      result.push({ ar: w, br: pi < parts.length - 1 && wi === words.length - 1, part: pi });
    });
  });
  return result;
}

const GRAMMAR_COLORS = {
  noun:      { bg: 'rgba(59,130,246,0.18)', border: 'rgba(59,130,246,0.5)', text: '#93c5fd', label: 'Noun · اسم' },
  verb:      { bg: 'rgba(239,68,68,0.15)',  border: 'rgba(239,68,68,0.5)',  text: '#fca5a5', label: 'Verb · فعل' },
  particle:  { bg: 'rgba(234,179,8,0.15)',  border: 'rgba(234,179,8,0.5)',  text: '#fde68a', label: 'Particle · حرف' },
  adjective: { bg: 'rgba(34,197,94,0.15)',  border: 'rgba(34,197,94,0.5)',  text: '#86efac', label: 'Adj · صفة' },
};

// ── DB-driven study tool components ──────────────────────────────────────────

function DbClozeExercise({ verseWords, analysis }) {
  const vocab = (analysis?.vocabulary || []).slice(0, 6);
  const blanks = useMemo(() => {
    const idxs = [];
    vocab.forEach(v => {
      const normV = normalizeArabicWord(v.wordAr);
      let idx = verseWords.findIndex((w, i) => normalizeArabicWord(w.ar) === normV && !idxs.includes(i));
      if (idx === -1 && normV.length >= 3) {
        idx = verseWords.findIndex((w, i) => normalizeArabicWord(w.ar).startsWith(normV) && !idxs.includes(i));
      }
      if (idx !== -1) idxs.push(idx);
    });
    return idxs;
  }, [verseWords, vocab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shuffle so answer order doesn't mirror verse order
  const options = useMemo(() => {
    const words = blanks.map(i => verseWords[i]?.ar).filter(Boolean);
    for (let i = words.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1)); // eslint-disable-line
      [words[i], words[j]] = [words[j], words[i]];
    }
    return words;
  }, [blanks]); // eslint-disable-line react-hooks/exhaustive-deps

  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);
  const [checked, setChecked] = useState(false);
  const placed = Object.values(answers);

  function handleChip(word) {
    if (checked) setChecked(false); // resume editing after a check
    setSelected(s => s === word ? null : word);
  }
  function handleBlank(idx) {
    if (checked) setChecked(false); // resume editing after a check
    if (selected) {
      setAnswers(prev => ({ ...prev, [idx]: selected }));
      setSelected(null);
    } else if (answers[idx]) {
      // tap a filled blank with no chip selected → remove the answer
      setAnswers(prev => { const n = { ...prev }; delete n[idx]; return n; });
    }
  }
  function reset() { setAnswers({}); setSelected(null); setChecked(false); }

  if (!analysis) return (
    <div style={{ textAlign: 'center', padding: '1.25rem 0' }}>
      <div style={{ width: 18, height: 18, margin: '0 auto', border: '2px solid var(--border2)', borderTopColor: 'var(--em)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );
  if (!blanks.length) return (
    <div style={{ color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem', textAlign: 'center', padding: '0.5rem 0' }}>
      No exercise ready for this verse.
    </div>
  );

  const allFilled = blanks.every(i => answers[i]);
  const score = checked ? blanks.filter(i => answers[i] === verseWords[i]?.ar).length : 0;

  return (
    <div>
      {/* Verse with tap-to-fill blanks */}
      <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.2rem', direction: 'rtl', textAlign: 'center', lineHeight: 3.2, marginBottom: '0.4rem', padding: '0.75rem 1rem', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border2)', borderRadius: 4 }}>
        {verseWords.map((w, i) => (
          <span key={i}>
            {blanks.includes(i) ? (
              <span onClick={() => handleBlank(i)} style={{
                display: 'inline-block', minWidth: 80, textAlign: 'center',
                background: checked
                  ? (answers[i] === w.ar ? 'rgba(52,211,153,0.15)' : 'rgba(248,113,113,0.12)')
                  : answers[i] ? 'var(--gold-subtle)' : selected ? 'var(--em-subtle)' : 'var(--em-subtle)',
                border: `1px solid ${
                  checked
                    ? (answers[i] === w.ar ? 'rgba(52,211,153,0.5)' : 'rgba(248,113,113,0.5)')
                    : answers[i] ? 'var(--gold-line)' : selected ? 'var(--em-strong)' : 'var(--em-line)'
                }`,
                borderRadius: 6,
                color: answers[i] ? 'var(--gold)' : 'var(--text3)',
                cursor: 'pointer', padding: '0.05rem 0.5rem',
                fontSize: answers[i] ? '1.15rem' : '0.6rem',
                fontFamily: answers[i] ? "'Noto Naskh Arabic', serif" : "'Josefin Sans', sans-serif",
                letterSpacing: answers[i] ? 'normal' : '0.06em',
                verticalAlign: 'middle',
                transition: 'all 0.15s',
              }}>{answers[i] || 'TAP'}</span>
            ) : <span>{w.ar}</span>}
            {w.br ? <br /> : (i < verseWords.length - 1 ? ' ' : '')}
          </span>
        ))}
      </div>
      {/* Contextual hint */}
      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.55rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)', textAlign: 'center', marginBottom: '0.85rem' }}>
        {selected ? '→ Now tap a blank above' : 'Tap a word to select, then tap a blank'}
      </div>
      {/* Word chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center', marginBottom: '1rem' }}>
        {options.map((word, i) => {
          const used = placed.includes(word);
          const sel = selected === word;
          return (
            <button key={i} onClick={() => !used && handleChip(word)} style={{
              fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.1rem', direction: 'rtl',
              padding: '0.4rem 0.85rem',
              background: sel ? 'var(--em-medium)' : used ? 'transparent' : 'var(--bg3)',
              border: `1px solid ${sel ? 'var(--em)' : used ? 'var(--border2)' : 'var(--border)'}`,
              borderRadius: 20, color: used ? 'var(--text3)' : 'var(--text)',
              cursor: used ? 'default' : 'pointer', opacity: used ? 0.4 : 1,
              transition: 'all 0.15s', minHeight: 40,
            }}>{word}</button>
          );
        })}
      </div>
      {/* Result */}
      {checked && (
        <div style={{ textAlign: 'center', marginBottom: '0.65rem', fontSize: '0.85rem', color: score === blanks.length ? '#34d399' : '#f87171' }}>
          {score === blanks.length ? '✓ Perfect!' : `${score} of ${blanks.length} correct — tap any blank to keep trying.`}
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button onClick={() => allFilled && !checked && setChecked(true)} disabled={!allFilled || checked} style={{
          flex: 1, padding: '0.65rem',
          background: allFilled && !checked ? 'rgba(140,207,225,0.12)' : 'transparent',
          border: `1px solid ${allFilled && !checked ? 'var(--em)' : 'var(--border)'}`,
          borderRadius: 4, fontFamily: "'Josefin Sans', sans-serif",
          fontSize: '0.65rem', letterSpacing: '0.12em', textTransform: 'uppercase',
          color: allFilled && !checked ? 'var(--em)' : 'var(--text3)',
          cursor: allFilled && !checked ? 'pointer' : 'default', minHeight: 40,
        }}>Check Answer</button>
        <button onClick={reset} style={{
          padding: '0.65rem 1rem', background: 'transparent',
          border: '1px solid var(--border)', borderRadius: 4,
          fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.65rem', letterSpacing: '0.12em',
          textTransform: 'uppercase', color: 'var(--text3)', cursor: 'pointer', minHeight: 40,
        }}>Reset</button>
      </div>
    </div>
  );
}

function DbHarakatTrainer({ verseWords }) {
  const [revealed, setRevealed] = useState(new Set());
  function toggle(i) {
    setRevealed(prev => { const n = new Set(prev); n.has(i) ? n.delete(i) : n.add(i); return n; });
  }
  function revealAll() { setRevealed(new Set(verseWords.map((_, i) => i))); }
  function reset() { setRevealed(new Set()); }

  return (
    <div>
      <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.3rem', direction: 'rtl', textAlign: 'center', lineHeight: 2.5, marginBottom: '0.75rem', padding: '0.75rem 1rem', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border2)', borderRadius: 4 }}>
        {verseWords.map((w, i) => {
          const isRev = revealed.has(i);
          return (
            <span key={i}>
              <span onClick={() => toggle(i)} style={{ cursor: 'pointer', color: isRev ? 'var(--text)' : 'var(--text2)', borderBottom: isRev ? 'none' : '1px dotted var(--em)', padding: '0 0.1rem', transition: 'color 0.2s' }}>
                {isRev ? w.ar : stripHarakat(w.ar)}
              </span>
              {w.br ? <br /> : (i < verseWords.length - 1 ? ' ' : '')}
            </span>
          );
        })}
      </div>
      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)', textAlign: 'center', marginBottom: '0.85rem' }}>Tap any word to reveal its vowel marks</div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button onClick={revealAll} style={{ flex: 1, padding: '0.65rem', background: 'rgba(140,207,225,0.1)', border: '1px solid var(--em)', borderRadius: 4, fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.65rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--em)', cursor: 'pointer', minHeight: 40 }}>Reveal All</button>
        <button onClick={reset} style={{ padding: '0.65rem 1rem', background: 'transparent', border: '1px solid var(--border)', borderRadius: 4, fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.65rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text3)', cursor: 'pointer', minHeight: 40 }}>Reset</button>
      </div>
    </div>
  );
}

function DbGrammarLayer({ verseWords, analysis }) {
  const [active, setActive] = useState(null);
  const tokens = analysis?.grammarTokens || [];
  const tokenMap = useMemo(() => {
    const m = {};
    tokens.filter(Boolean).forEach(t => { m[t.ar] = t; });
    return m;
  }, [tokens]);

  const legend = [
    { type: 'noun', color: '#93c5fd', label: 'Noun · اسم' },
    { type: 'verb', color: '#fca5a5', label: 'Verb · فعل' },
    { type: 'particle', color: '#fde68a', label: 'Particle · حرف' },
    { type: 'adjective', color: '#86efac', label: 'Adj · صفة' },
  ];

  if (!tokens.length) return (
    <div style={{ color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem', textAlign: 'center', padding: '0.5rem 0' }}>
      Grammar data updating — this verse will refresh on next visit.
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.85rem' }}>
        {legend.map(l => (
          <div key={l.type} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: l.color }} />
            <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text3)' }}>{l.label}</span>
          </div>
        ))}
      </div>
      <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.25rem', direction: 'rtl', textAlign: 'center', lineHeight: 2.5, marginBottom: '0.75rem', padding: '0.75rem 1rem', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border2)', borderRadius: 4 }}>
        {verseWords.map((w, i) => {
          const t = tokenMap[w.ar];
          const c = t ? GRAMMAR_COLORS[t.type] : null;
          const isActive = active?._idx === i;
          return (
            <span key={i}>
              <span onClick={() => setActive(isActive ? null : { ...w, _idx: i })} style={{ cursor: 'pointer', color: c ? c.text : 'var(--text)', background: isActive ? (c?.bg || 'transparent') : 'transparent', border: isActive ? `1px solid ${c?.border || 'var(--border)'}` : '1px solid transparent', borderRadius: 3, padding: '0.05rem 0.1rem', transition: 'all 0.15s' }}>
                {w.ar}
              </span>
              {w.br ? <br /> : (i < verseWords.length - 1 ? ' ' : '')}
            </span>
          );
        })}
      </div>
      {active && (() => {
        const t = tokenMap[active.ar];
        const c = t ? GRAMMAR_COLORS[t.type] : null;
        return (
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 4, padding: '0.75rem 1rem', animation: 'fadeUp 0.15s ease' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', justifyContent: 'flex-end', direction: 'rtl', marginBottom: '0.4rem' }}>
              <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.3rem', color: 'var(--gold)' }}>{active.ar}</div>
              {c && <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: c.text, background: c.bg, border: `1px solid ${c.border}`, borderRadius: 3, padding: '0.2rem 0.4rem' }}>{c.label}</div>}
            </div>
            {t && <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.9rem', direction: 'rtl', textAlign: 'right', color: 'var(--text2)', lineHeight: 1.6, padding: '0.4rem 0.6rem', background: 'rgba(229,150,83,0.06)', border: '1px solid rgba(229,150,83,0.11)', borderRadius: 3 }}>{t.roleAr}</div>}
          </div>
        );
      })()}
    </div>
  );
}

function DbRootExplorer({ analysis }) {
  const families = analysis?.rootFamilies || [];
  const [activeRoot, setActiveRoot] = useState(null);
  const effectiveRoot = activeRoot ?? families[0]?.root ?? null;
  const family = families.find(f => f.root === effectiveRoot)?.words || [];

  if (!families.length) return (
    <div style={{ color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem', textAlign: 'center', padding: '0.5rem 0' }}>
      Root families updating — this verse will refresh on next visit.
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1rem' }}>
        {families.map(fam => (
          <button key={fam.root} onClick={() => setActiveRoot(fam.root)} style={{
            fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.95rem', direction: 'rtl',
            padding: '0.35rem 0.75rem',
            background: effectiveRoot === fam.root ? 'rgba(229,150,83,0.16)' : 'var(--bg3)',
            border: `1px solid ${effectiveRoot === fam.root ? 'rgba(229,150,83,0.62)' : 'var(--border)'}`,
            borderRadius: 20, color: effectiveRoot === fam.root ? 'var(--gold)' : 'var(--text2)',
            cursor: 'pointer', minHeight: 34,
          }}>{fam.root}</button>
        ))}
      </div>
      {effectiveRoot && (
        <>
          <div style={{ textAlign: 'center', marginBottom: '1rem', padding: '0.85rem', background: 'rgba(229,150,83,0.06)', border: '1px solid rgba(229,150,83,0.16)', borderRadius: 4 }}>
            <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '2.4rem', color: 'var(--gold)', direction: 'rtl', lineHeight: 1.2, marginBottom: '0.2rem' }}>{effectiveRoot}</div>
            <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.55rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text3)' }}>Three-Letter Root</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
            {family.map((item, i) => (
              <div key={i} style={{ background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 4, padding: '0.6rem 0.75rem' }}>
                <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.1rem', color: 'var(--text)', direction: 'rtl', textAlign: 'right', marginBottom: '0.15rem' }}>{item.ar}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text3)', fontStyle: 'italic' }}>{item.meaningEn}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function seededShuffle(arr, seed) {
  const a = [...arr];
  let s = seed;
  for (let i = a.length - 1; i > 0; i--) {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    const j = Math.abs(s) % (i + 1);
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ── Root Drill ────────────────────────────────────────────────────────────────
function DbRootDrill({ analysis }) {
  const vocab = useMemo(
    () => (analysis?.vocabulary || []).filter(v => v?.rootLetters && v?.wordAr && v?.meaningEn),
    [analysis]
  );

  const [qIdx, setQIdx] = useState(0);
  const [chosen, setChosen] = useState(null);
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);

  const questions = useMemo(() => {
    if (vocab.length < 2) return [];
    return vocab.map((v, i) => {
      const correct = v.rootLetters;
      const pool = vocab.filter(x => x.rootLetters !== correct).map(x => x.rootLetters);
      const uniq = [...new Set(pool)];
      const distractors = seededShuffle(uniq, i * 31 + 7).slice(0, 3);
      while (distractors.length < 3) distractors.push('—');
      return { word: v.wordAr, meaning: v.meaningEn, correct, options: seededShuffle([correct, ...distractors], i * 97) };
    });
  }, [vocab]);

  if (!questions.length) return (
    <div style={{ color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem', textAlign: 'center', padding: '0.5rem 0' }}>
      Not enough vocabulary data — open this verse in Study tab first.
    </div>
  );

  const q = questions[qIdx % questions.length];
  const done = chosen !== null;
  const isCorrect = chosen === q.correct;

  function pick(opt) {
    if (done || opt === '—') return;
    setChosen(opt);
    setTotal(t => t + 1);
    if (opt === q.correct) setScore(s => s + 1);
  }

  function next() {
    setChosen(null);
    setQIdx(i => (i + 1) % questions.length);
    if (qIdx + 1 >= questions.length) { setScore(0); setTotal(0); }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.52rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text3)' }}>
          {qIdx + 1} / {questions.length}
        </div>
        {total > 0 && (
          <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.08em', color: score === total ? '#34d399' : 'var(--gold)' }}>
            {score} / {total} correct
          </div>
        )}
      </div>
      <div style={{ textAlign: 'center', marginBottom: '1.25rem', padding: '1.1rem 0.75rem', background: 'rgba(229,150,83,0.05)', border: '1px solid var(--border)', borderRadius: 6 }}>
        <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.48rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: '0.55rem' }}>
          What is the three-letter root of
        </div>
        <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '2rem', color: 'var(--gold)', direction: 'rtl', lineHeight: 1.2, marginBottom: '0.3rem' }}>
          {q.word}
        </div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text3)', fontStyle: 'italic' }}>{q.meaning}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.45rem', marginBottom: '0.85rem' }}>
        {q.options.map(opt => {
          let bg = 'var(--bg3)', border = 'var(--border)', color = 'var(--text)';
          if (done) {
            if (opt === q.correct)  { bg = 'rgba(52,211,153,0.12)';  border = 'rgba(52,211,153,0.5)';  color = '#34d399'; }
            else if (opt === chosen) { bg = 'rgba(248,113,113,0.1)'; border = 'rgba(248,113,113,0.45)'; color = '#f87171'; }
          }
          return (
            <button key={opt} onClick={() => pick(opt)} style={{
              fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.1rem', direction: 'rtl',
              padding: '0.6rem 0.5rem', background: bg, border: `1px solid ${border}`,
              borderRadius: 5, color, cursor: (done || opt === '—') ? 'default' : 'pointer',
              transition: 'all 0.15s', textAlign: 'center', minHeight: 44,
            }}>{opt}</button>
          );
        })}
      </div>
      {done && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '0.8rem', color: isCorrect ? '#34d399' : '#f87171', fontStyle: 'italic' }}>
            {isCorrect ? 'Correct!' : `Root is ${q.correct}`}
          </div>
          <button onClick={next} className="pill-btn" style={{ fontSize: '0.6rem' }}>
            {qIdx + 1 < questions.length ? 'Next →' : 'Restart ↺'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Match Game ────────────────────────────────────────────────────────────────
function DbMatchGame({ analysis }) {
  const allPairs = useMemo(
    () => seededShuffle((analysis?.vocabulary || []).filter(v => v?.wordAr && v?.meaningEn), 42),
    [analysis]
  );
  const [page, setPage] = useState(0);

  const pairs = allPairs.slice(page * 5, page * 5 + 5);
  const leftWords  = useMemo(() => seededShuffle(pairs.map(p => p.wordAr), page * 13), [pairs, page]);
  const rightItems = useMemo(() => seededShuffle(pairs.map(p => ({ wordAr: p.wordAr, meaningEn: p.meaningEn })), page * 71), [pairs, page]);

  const [selLeft, setSelLeft]   = useState(null);
  const [matched, setMatched]   = useState({});
  const [wrongPair, setWrongPair] = useState(null);

  const allMatched = pairs.length > 0 && Object.keys(matched).length >= pairs.length;

  function pickLeft(word) {
    if (matched[word] || wrongPair) return;
    setSelLeft(s => s === word ? null : word);
  }

  function pickRight(item) {
    if (matched[item.wordAr] || wrongPair || !selLeft) return;
    if (selLeft === item.wordAr) {
      setMatched(m => ({ ...m, [item.wordAr]: true }));
      setSelLeft(null);
    } else {
      setWrongPair({ left: selLeft, right: item.wordAr });
      setTimeout(() => { setWrongPair(null); setSelLeft(null); }, 650);
    }
  }

  function nextPage() {
    setPage(p => p + 1 < Math.ceil(allPairs.length / 5) ? p + 1 : 0);
    setMatched({});
    setSelLeft(null);
  }

  if (!pairs.length) return (
    <div style={{ color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem', textAlign: 'center', padding: '0.5rem 0' }}>
      Vocabulary data updating — open this verse in Study tab first.
    </div>
  );

  return (
    <div>
      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.48rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text3)', textAlign: 'center', marginBottom: '0.85rem' }}>
        {allMatched ? '✓ All matched!' : selLeft ? '→ Now tap its English meaning' : 'Tap an Arabic word, then its meaning'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
          {leftWords.map(word => {
            const isMatched = matched[word];
            const isSel = selLeft === word;
            const isWrong = wrongPair?.left === word;
            let bg = 'var(--bg3)', border = 'var(--border)', color = 'var(--text)';
            if (isMatched)      { bg = 'rgba(52,211,153,0.1)'; border = 'rgba(52,211,153,0.4)'; color = '#34d399'; }
            else if (isWrong)   { bg = 'rgba(248,113,113,0.1)'; border = 'rgba(248,113,113,0.45)'; color = '#f87171'; }
            else if (isSel)     { bg = 'rgba(229,150,83,0.14)'; border = 'rgba(229,150,83,0.6)'; color = 'var(--gold)'; }
            return (
              <button key={word} onClick={() => pickLeft(word)} style={{
                fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1rem', direction: 'rtl',
                padding: '0.5rem 0.55rem', background: bg, border: `1px solid ${border}`,
                borderRadius: 5, color, cursor: isMatched ? 'default' : 'pointer',
                transition: 'all 0.15s', textAlign: 'center', minHeight: 40,
              }}>{word}</button>
            );
          })}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
          {rightItems.map(item => {
            const isMatched = matched[item.wordAr];
            const isWrong = wrongPair?.right === item.wordAr;
            let bg = 'var(--bg3)', border = 'var(--border)', color = 'var(--text2)';
            if (isMatched)    { bg = 'rgba(52,211,153,0.1)'; border = 'rgba(52,211,153,0.4)'; color = '#34d399'; }
            else if (isWrong) { bg = 'rgba(248,113,113,0.1)'; border = 'rgba(248,113,113,0.45)'; color = '#f87171'; }
            else if (selLeft) { bg = 'rgba(140,207,225,0.05)'; border = 'rgba(140,207,225,0.3)'; }
            return (
              <button key={item.wordAr} onClick={() => pickRight(item)} style={{
                fontSize: '0.72rem', padding: '0.5rem 0.55rem', background: bg,
                border: `1px solid ${border}`, borderRadius: 5, color,
                cursor: isMatched ? 'default' : 'pointer',
                transition: 'all 0.15s', textAlign: 'center', minHeight: 40, lineHeight: 1.4,
              }}>{item.meaningEn}</button>
            );
          })}
        </div>
      </div>
      {allMatched && (
        <button onClick={nextPage} className="pill-btn" style={{ marginTop: '0.85rem', width: '100%', fontSize: '0.6rem' }}>
          {page + 1 < Math.ceil(allPairs.length / 5) ? `Next set →` : 'Restart ↺'}
        </button>
      )}
    </div>
  );
}

// ── Drills panel (tab-switcher wrapping all drill exercises) ──────────────────
// ── Pattern (وزن) Drill ───────────────────────────────────────────────────────
function DbWaznDrill({ analysis }) {
  const vocab = useMemo(
    () => (analysis?.vocabulary || []).filter(v => v?.wazn && v?.wazn.trim() && v?.wordAr),
    [analysis]
  );
  const [qIdx, setQIdx] = useState(0);
  const [chosen, setChosen] = useState(null);
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);

  const questions = useMemo(() => {
    if (vocab.length < 2) return [];
    return vocab.map((v, i) => {
      const correct = v.wazn;
      const pool = [...new Set(vocab.filter(x => x.wazn !== correct).map(x => x.wazn))];
      const distractors = seededShuffle(pool, i * 53).slice(0, 3);
      while (distractors.length < 3) distractors.push(['فَعَلَ', 'فَاعِل', 'مَفعُول', 'فُعُول'].find(p => p !== correct && !distractors.includes(p)) || 'فِعَال');
      return { word: v.wordAr, meaning: v.meaningEn, correct, options: seededShuffle([correct, ...distractors.slice(0, 3)], i * 89) };
    });
  }, [vocab]);

  if (!questions.length) return (
    <div style={{ color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem', textAlign: 'center', padding: '0.5rem 0' }}>
      Pattern data available on next verse analysis — open Study tab on a new verse.
    </div>
  );

  const q = questions[qIdx % questions.length];
  const done = chosen !== null;
  function pick(opt) { if (done) return; setChosen(opt); setTotal(t => t + 1); if (opt === q.correct) setScore(s => s + 1); }
  function next() { setChosen(null); setQIdx(i => (i + 1) % questions.length); if (qIdx + 1 >= questions.length) { setScore(0); setTotal(0); } }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.52rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text3)' }}>{qIdx + 1} / {questions.length}</div>
        {total > 0 && <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', color: score === total ? '#34d399' : 'var(--gold)' }}>{score}/{total}</div>}
      </div>
      <div style={{ textAlign: 'center', marginBottom: '1.25rem', padding: '1rem 0.75rem', background: 'rgba(229,150,83,0.05)', border: '1px solid var(--border)', borderRadius: 6 }}>
        <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.48rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: '0.5rem' }}>What is the وزن pattern of</div>
        <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '2rem', color: 'var(--gold)', direction: 'rtl', lineHeight: 1.2, marginBottom: '0.25rem' }}>{q.word}</div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text3)', fontStyle: 'italic' }}>{q.meaning}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.45rem', marginBottom: '0.85rem' }}>
        {q.options.map(opt => {
          let bg = 'var(--bg3)', border = 'var(--border)', color = 'var(--text)';
          if (done) { if (opt === q.correct) { bg = 'rgba(52,211,153,0.12)'; border = 'rgba(52,211,153,0.5)'; color = '#34d399'; } else if (opt === chosen) { bg = 'rgba(248,113,113,0.1)'; border = 'rgba(248,113,113,0.45)'; color = '#f87171'; } }
          return <button key={opt} onClick={() => pick(opt)} style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.1rem', direction: 'rtl', padding: '0.6rem 0.5rem', background: bg, border: `1px solid ${border}`, borderRadius: 5, color, cursor: done ? 'default' : 'pointer', transition: 'all 0.15s', textAlign: 'center', minHeight: 44 }}>{opt}</button>;
        })}
      </div>
      {done && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: '0.8rem', color: chosen === q.correct ? '#34d399' : '#f87171', fontStyle: 'italic' }}>{chosen === q.correct ? 'Correct!' : `Pattern: ${q.correct}`}</div>
        <button onClick={next} className="pill-btn" style={{ fontSize: '0.6rem' }}>{qIdx + 1 < questions.length ? 'Next →' : 'Restart ↺'}</button>
      </div>}
    </div>
  );
}

// ── POS Sort ──────────────────────────────────────────────────────────────────
function DbPOSSort({ verseWords, analysis }) {
  const tokens = analysis?.grammarTokens || [];
  const BUCKETS = ['noun', 'verb', 'adjective', 'particle'];

  const chips = useMemo(() => {
    const seen = new Set();
    return verseWords.filter(w => {
      const gt = tokens.find(t => normalizeArabicWord(t.ar) === normalizeArabicWord(w.ar));
      if (!gt || seen.has(w.ar)) return false;
      seen.add(w.ar);
      return true;
    }).map(w => ({ ar: w.ar, correct: tokens.find(t => normalizeArabicWord(t.ar) === normalizeArabicWord(w.ar))?.type }));
  }, [verseWords, tokens]);

  const [placed, setPlaced] = useState({});
  const [selChip, setSelChip] = useState(null);
  const [checked, setChecked] = useState(false);

  const unplaced = chips.filter(c => !placed[c.ar]);
  const allPlaced = chips.length > 0 && unplaced.length === 0;
  const score = checked ? chips.filter(c => placed[c.ar] === c.correct).length : 0;

  function selectChip(ar) { if (checked) return; setSelChip(s => s === ar ? null : ar); }
  function placeToBucket(bucket) {
    if (!selChip || checked) return;
    setPlaced(p => ({ ...p, [selChip]: bucket }));
    setSelChip(null);
  }
  function reset() { setPlaced({}); setSelChip(null); setChecked(false); }

  if (!chips.length) return <div style={{ color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem', textAlign: 'center', padding: '0.5rem 0' }}>Grammar data updating — open Study tab first.</div>;

  return (
    <div>
      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.48rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text3)', textAlign: 'center', marginBottom: '0.85rem' }}>
        {checked ? `${score} / ${chips.length} correct` : selChip ? '→ Tap a category below' : 'Tap a word then its grammatical category'}
      </div>
      {/* Buckets */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.4rem', marginBottom: '0.85rem' }}>
        {BUCKETS.map(bucket => {
          const c = GRAMMAR_COLORS[bucket];
          const words = chips.filter(ch => placed[ch.ar] === bucket);
          return (
            <div key={bucket} onClick={() => placeToBucket(bucket)} style={{ background: selChip ? c.bg : 'var(--bg3)', border: `1px solid ${selChip ? c.border : 'var(--border)'}`, borderRadius: 5, padding: '0.5rem', minHeight: 54, cursor: selChip ? 'pointer' : 'default', transition: 'all 0.15s' }}>
              <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.5rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: c.text, marginBottom: '0.35rem' }}>{c.label}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                {words.map(ch => {
                  const ok = checked ? ch.correct === bucket : null;
                  return <span key={ch.ar} style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.9rem', direction: 'rtl', color: ok === null ? c.text : ok ? '#34d399' : '#f87171', background: ok === null ? c.bg : ok ? 'rgba(52,211,153,0.12)' : 'rgba(248,113,113,0.1)', border: `1px solid ${ok === null ? c.border : ok ? 'rgba(52,211,153,0.4)' : 'rgba(248,113,113,0.4)'}`, borderRadius: 3, padding: '0.1rem 0.4rem' }}>{ch.ar}</span>;
                })}
              </div>
            </div>
          );
        })}
      </div>
      {/* Unplaced chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', justifyContent: 'center', marginBottom: '0.75rem', minHeight: 36 }}>
        {unplaced.map(ch => (
          <button key={ch.ar} onClick={() => selectChip(ch.ar)} style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1rem', direction: 'rtl', padding: '0.3rem 0.7rem', borderRadius: 20, background: selChip === ch.ar ? 'rgba(229,150,83,0.16)' : 'var(--bg3)', border: `1px solid ${selChip === ch.ar ? 'rgba(229,150,83,0.6)' : 'var(--border)'}`, color: selChip === ch.ar ? 'var(--gold)' : 'var(--text)', cursor: 'pointer', transition: 'all 0.15s' }}>{ch.ar}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
        {allPlaced && !checked && <button onClick={() => setChecked(true)} className="pill-btn" style={{ fontSize: '0.6rem' }}>Check ✓</button>}
        {checked && <button onClick={reset} className="pill-btn" style={{ fontSize: '0.6rem' }}>Try again ↺</button>}
      </div>
    </div>
  );
}

// ── Ṣadr/ʿAjz Pairing ────────────────────────────────────────────────────────
function DbSadrAjzPair({ displayVerses, verseIdx }) {
  const pairs = useMemo(() => {
    const start = Math.max(0, verseIdx - 1);
    const slice = displayVerses.slice(start, start + 4).filter(v => v?.textAr?.includes(' / '));
    return slice.map(v => {
      const idx = v.textAr.indexOf(' / ');
      return { sadr: v.textAr.slice(0, idx).trim(), ajz: v.textAr.slice(idx + 3).trim() };
    }).slice(0, 3);
  }, [displayVerses, verseIdx]);

  const sadrs  = useMemo(() => seededShuffle(pairs.map((p, i) => ({ text: p.sadr, idx: i })), verseIdx * 17), [pairs, verseIdx]);
  const ajzs   = useMemo(() => seededShuffle(pairs.map((p, i) => ({ text: p.ajz,  idx: i })), verseIdx * 41), [pairs, verseIdx]);

  const [selSadr, setSelSadr] = useState(null);
  const [matched, setMatched] = useState({});
  const [wrongPair, setWrongPair] = useState(null);

  const allMatched = pairs.length > 0 && Object.keys(matched).length >= pairs.length;

  function pickSadr(item) { if (matched[item.idx] !== undefined || wrongPair) return; setSelSadr(s => s?.idx === item.idx ? null : item); }
  function pickAjz(item) {
    if (matched[item.idx] !== undefined || wrongPair || !selSadr) return;
    if (selSadr.idx === item.idx) { setMatched(m => ({ ...m, [item.idx]: true })); setSelSadr(null); }
    else { setWrongPair({ s: selSadr.idx, a: item.idx }); setTimeout(() => { setWrongPair(null); setSelSadr(null); }, 700); }
  }

  if (pairs.length < 2) return <div style={{ color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem', textAlign: 'center', padding: '0.5rem 0' }}>Need at least 2 verses with a caesura — navigate to a verse in the middle of the poem.</div>;

  const colStyle = { display: 'flex', flexDirection: 'column', gap: '0.4rem' };
  return (
    <div>
      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.48rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text3)', textAlign: 'center', marginBottom: '0.85rem' }}>
        {allMatched ? '✓ All pairs matched!' : selSadr ? '→ Now tap the matching ʿajz' : 'Tap a ṣadr, then its matching ʿajz'}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
        <div style={colStyle}>
          {sadrs.map(item => {
            const isM = matched[item.idx] !== undefined;
            const isSel = selSadr?.idx === item.idx;
            const isW = wrongPair?.s === item.idx;
            let bg = 'var(--bg3)', border = 'var(--border)', color = 'var(--text)';
            if (isM) { bg = 'rgba(52,211,153,0.1)'; border = 'rgba(52,211,153,0.4)'; color = '#34d399'; }
            else if (isW) { bg = 'rgba(248,113,113,0.1)'; border = 'rgba(248,113,113,0.4)'; color = '#f87171'; }
            else if (isSel) { bg = 'rgba(229,150,83,0.14)'; border = 'rgba(229,150,83,0.6)'; color = 'var(--gold)'; }
            return <button key={item.idx} onClick={() => pickSadr(item)} style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.85rem', direction: 'rtl', padding: '0.55rem 0.6rem', background: bg, border: `1px solid ${border}`, borderRadius: 5, color, cursor: isM ? 'default' : 'pointer', transition: 'all 0.15s', textAlign: 'right', lineHeight: 1.55 }}>{item.text}</button>;
          })}
        </div>
        <div style={colStyle}>
          {ajzs.map(item => {
            const isM = matched[item.idx] !== undefined;
            const isW = wrongPair?.a === item.idx;
            let bg = 'var(--bg3)', border = 'var(--border)', color = 'var(--text2)';
            if (isM) { bg = 'rgba(52,211,153,0.1)'; border = 'rgba(52,211,153,0.4)'; color = '#34d399'; }
            else if (isW) { bg = 'rgba(248,113,113,0.1)'; border = 'rgba(248,113,113,0.4)'; color = '#f87171'; }
            else if (selSadr) { bg = 'rgba(140,207,225,0.05)'; border = 'rgba(140,207,225,0.3)'; }
            return <button key={item.idx} onClick={() => pickAjz(item)} style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.85rem', direction: 'rtl', padding: '0.55rem 0.6rem', background: bg, border: `1px solid ${border}`, borderRadius: 5, color, cursor: isM ? 'default' : 'pointer', transition: 'all 0.15s', textAlign: 'right', lineHeight: 1.55 }}>{item.text}</button>;
          })}
        </div>
      </div>
      {allMatched && <button onClick={() => { setMatched({}); setSelSadr(null); }} className="pill-btn" style={{ marginTop: '0.85rem', width: '100%', fontSize: '0.6rem' }}>Play again ↺</button>}
    </div>
  );
}

// ── Broken Plural Spotter ─────────────────────────────────────────────────────
function DbBrokenPluralSpotter({ analysis }) {
  const nouns = useMemo(() => {
    const tokens = analysis?.grammarTokens || [];
    return tokens.filter(t => t.type === 'noun' && t.ar?.length > 2);
  }, [analysis]);

  // Heuristic for old analyses without isBrokenPlural flag:
  // sound plurals end in ون, ين, ات (after stripping harakat)
  function looksSound(ar) { const s = stripHarakat(ar); return /(?:ون|ين|ات)$/.test(s); }
  function isBroken(t) { return t.isBrokenPlural === true || (t.isBrokenPlural === undefined && !looksSound(t.ar) && stripHarakat(t.ar).length >= 4); }

  const brokenCount = nouns.filter(isBroken).length;

  const [tapped, setTapped] = useState(new Set());
  const [checked, setChecked] = useState(false);

  const score = checked ? [...tapped].filter(ar => nouns.find(n => n.ar === ar && isBroken(n))).length : 0;
  const fp    = checked ? [...tapped].filter(ar => nouns.find(n => n.ar === ar && !isBroken(n))).length : 0;

  if (!nouns.length) return <div style={{ color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem', textAlign: 'center', padding: '0.5rem 0' }}>No nouns tagged in this verse.</div>;
  if (!brokenCount)  return <div style={{ color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem', textAlign: 'center', padding: '0.5rem 0' }}>No broken plurals in this verse — all nouns use sound patterns.</div>;

  function toggle(ar) { if (checked) return; setTapped(s => { const n = new Set(s); n.has(ar) ? n.delete(ar) : n.add(ar); return n; }); }

  return (
    <div>
      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.48rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text3)', textAlign: 'center', marginBottom: '0.85rem' }}>
        {checked ? `${score} broken plural${score !== 1 ? 's' : ''} found · ${fp} false positive${fp !== 1 ? 's' : ''}` : `Tap the broken plural${brokenCount > 1 ? 's' : ''} جمع تكسير`}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center', marginBottom: '1rem' }}>
        {nouns.map((t, i) => {
          const sel = tapped.has(t.ar);
          const broken = isBroken(t);
          let bg = 'var(--bg3)', border = 'var(--border)', color = 'var(--text)';
          if (checked && sel && broken)    { bg = 'rgba(52,211,153,0.12)'; border = 'rgba(52,211,153,0.5)'; color = '#34d399'; }
          else if (checked && sel && !broken) { bg = 'rgba(248,113,113,0.1)'; border = 'rgba(248,113,113,0.45)'; color = '#f87171'; }
          else if (checked && !sel && broken) { bg = 'rgba(229,150,83,0.12)'; border = 'rgba(229,150,83,0.5)'; color = 'var(--gold)'; }
          else if (sel) { bg = 'rgba(140,207,225,0.1)'; border = 'rgba(140,207,225,0.5)'; color = 'var(--em)'; }
          return <button key={i} onClick={() => toggle(t.ar)} style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.1rem', direction: 'rtl', padding: '0.4rem 0.85rem', borderRadius: 20, background: bg, border: `1px solid ${border}`, color, cursor: checked ? 'default' : 'pointer', transition: 'all 0.15s' }}>{t.ar}</button>;
        })}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
        {!checked && tapped.size > 0 && <button onClick={() => setChecked(true)} className="pill-btn" style={{ fontSize: '0.6rem' }}>Check ✓</button>}
        {checked && <button onClick={() => { setTapped(new Set()); setChecked(false); }} className="pill-btn" style={{ fontSize: '0.6rem' }}>Try again ↺</button>}
      </div>
    </div>
  );
}

// ── Root Sort ─────────────────────────────────────────────────────────────────
function DbRootSort({ analysis }) {
  const vocab = useMemo(() => (analysis?.vocabulary || []).filter(v => v?.wordAr && v?.rootLetters), [analysis]);
  const roots = useMemo(() => [...new Set(vocab.map(v => v.rootLetters))], [vocab]);

  const [placed, setPlaced] = useState({});
  const [selWord, setSelWord] = useState(null);
  const [checked, setChecked] = useState(false);

  const unplaced = useMemo(() => seededShuffle(vocab.filter(v => !placed[v.wordAr]), 99), [vocab, placed]);
  const allPlaced = vocab.length > 0 && unplaced.length === 0;
  const score = checked ? vocab.filter(v => placed[v.wordAr] === v.rootLetters).length : 0;

  if (roots.length < 2) return <div style={{ color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem', textAlign: 'center', padding: '0.5rem 0' }}>Need at least 2 different roots in this verse for sorting.</div>;

  function selectWord(wordAr) { if (checked) return; setSelWord(s => s === wordAr ? null : wordAr); }
  function placeToRoot(root) { if (!selWord || checked) return; setPlaced(p => ({ ...p, [selWord]: root })); setSelWord(null); }
  function reset() { setPlaced({}); setSelWord(null); setChecked(false); }

  return (
    <div>
      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.48rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text3)', textAlign: 'center', marginBottom: '0.85rem' }}>
        {checked ? `${score} / ${vocab.length} correct` : selWord ? '→ Tap a root to file it under' : 'Tap a word then its three-letter root'}
      </div>
      {/* Root buckets */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '0.85rem' }}>
        {roots.map(root => {
          const words = vocab.filter(v => placed[v.wordAr] === root);
          return (
            <div key={root} onClick={() => placeToRoot(root)} style={{ background: selWord ? 'rgba(229,150,83,0.06)' : 'var(--bg3)', border: `1px solid ${selWord ? 'rgba(229,150,83,0.4)' : 'var(--border)'}`, borderRadius: 5, padding: '0.5rem 0.75rem', cursor: selWord ? 'pointer' : 'default', transition: 'all 0.15s', minHeight: 46 }}>
              <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.95rem', color: 'var(--gold)', direction: 'rtl', marginBottom: '0.3rem' }}>{root}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                {words.map(v => {
                  const ok = checked ? v.rootLetters === root : null;
                  return <span key={v.wordAr} style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.9rem', direction: 'rtl', padding: '0.1rem 0.5rem', borderRadius: 3, background: ok === null ? 'rgba(229,150,83,0.1)' : ok ? 'rgba(52,211,153,0.12)' : 'rgba(248,113,113,0.1)', border: `1px solid ${ok === null ? 'rgba(229,150,83,0.35)' : ok ? 'rgba(52,211,153,0.4)' : 'rgba(248,113,113,0.4)'}`, color: ok === null ? 'var(--text)' : ok ? '#34d399' : '#f87171' }}>{v.wordAr}</span>;
                })}
              </div>
            </div>
          );
        })}
      </div>
      {/* Unplaced word chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', justifyContent: 'center', marginBottom: '0.75rem', minHeight: 36 }}>
        {unplaced.map(v => (
          <button key={v.wordAr} onClick={() => selectWord(v.wordAr)} style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1rem', direction: 'rtl', padding: '0.3rem 0.7rem', borderRadius: 20, background: selWord === v.wordAr ? 'rgba(229,150,83,0.16)' : 'var(--bg3)', border: `1px solid ${selWord === v.wordAr ? 'rgba(229,150,83,0.6)' : 'var(--border)'}`, color: selWord === v.wordAr ? 'var(--gold)' : 'var(--text)', cursor: 'pointer', transition: 'all 0.15s' }}>{v.wordAr}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
        {allPlaced && !checked && <button onClick={() => setChecked(true)} className="pill-btn" style={{ fontSize: '0.6rem' }}>Check ✓</button>}
        {checked && <button onClick={reset} className="pill-btn" style={{ fontSize: '0.6rem' }}>Try again ↺</button>}
      </div>
    </div>
  );
}

// ── Synonym / Antonym Drill ───────────────────────────────────────────────────
function DbSynonymDrill({ analysis }) {
  const [mode, setMode] = useState('synonym');
  const vocab = useMemo(() => (analysis?.vocabulary || []).filter(v => v?.wordAr && v?.meaningEn), [analysis]);

  const questions = useMemo(() => {
    const qs = [];
    vocab.forEach((v, i) => {
      const syns = Array.isArray(v.synonyms) ? v.synonyms.filter(Boolean) : [];
      const ants = Array.isArray(v.antonyms) ? v.antonyms.filter(Boolean) : [];
      const pool = vocab.filter((_, j) => j !== i).map(x => x.wordAr);
      const distract = seededShuffle(pool, i * 61).slice(0, 3);
      if (syns.length) qs.push({ type: 'synonym', word: v.wordAr, meaning: v.meaningEn, correct: syns[0], options: seededShuffle([syns[0], ...distract.slice(0,3)], i * 37) });
      if (ants.length) qs.push({ type: 'antonym', word: v.wordAr, meaning: v.meaningEn, correct: ants[0], options: seededShuffle([ants[0], ...distract.slice(0,3)], i * 83) });
    });
    return qs;
  }, [vocab]);

  const filtered = questions.filter(q => q.type === mode);
  const [qIdx, setQIdx] = useState(0);
  const [chosen, setChosen] = useState(null);
  const [score, setScore] = useState(0);
  const [total, setTotal] = useState(0);

  function switchMode(m) { setMode(m); setQIdx(0); setChosen(null); setScore(0); setTotal(0); }

  if (!filtered.length) return (
    <div>
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.85rem', justifyContent: 'center' }}>
        {['synonym', 'antonym'].map(m => <button key={m} onClick={() => switchMode(m)} style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.52rem', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '0.28rem 0.75rem', borderRadius: 20, background: mode === m ? 'rgba(229,150,83,0.16)' : 'var(--bg3)', border: `1px solid ${mode === m ? 'rgba(229,150,83,0.6)' : 'var(--border)'}`, color: mode === m ? 'var(--gold)' : 'var(--text3)', cursor: 'pointer' }}>{m}</button>)}
      </div>
      <div style={{ color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem', textAlign: 'center', padding: '0.5rem 0' }}>
        {mode === 'synonym' ? 'No synonyms in this verse analysis yet.' : 'No antonyms in this verse analysis yet.'}
      </div>
    </div>
  );

  const q = filtered[qIdx % filtered.length];
  const done = chosen !== null;
  function pick(opt) { if (done) return; setChosen(opt); setTotal(t => t + 1); if (opt === q.correct) setScore(s => s + 1); }
  function next() { setChosen(null); setQIdx(i => (i + 1) % filtered.length); if (qIdx + 1 >= filtered.length) { setScore(0); setTotal(0); } }

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', justifyContent: 'center' }}>
        {['synonym', 'antonym'].map(m => <button key={m} onClick={() => switchMode(m)} style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.52rem', letterSpacing: '0.1em', textTransform: 'uppercase', padding: '0.28rem 0.75rem', borderRadius: 20, background: mode === m ? 'rgba(229,150,83,0.16)' : 'var(--bg3)', border: `1px solid ${mode === m ? 'rgba(229,150,83,0.6)' : 'var(--border)'}`, color: mode === m ? 'var(--gold)' : 'var(--text3)', cursor: 'pointer' }}>{m}</button>)}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.52rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text3)' }}>{qIdx + 1} / {filtered.length}</div>
        {total > 0 && <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', color: score === total ? '#34d399' : 'var(--gold)' }}>{score}/{total}</div>}
      </div>
      <div style={{ textAlign: 'center', marginBottom: '1.25rem', padding: '1rem 0.75rem', background: 'rgba(229,150,83,0.05)', border: '1px solid var(--border)', borderRadius: 6 }}>
        <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.48rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: '0.5rem' }}>
          {mode === 'synonym' ? 'Choose the synonym of' : 'Choose the antonym of'}
        </div>
        <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '2rem', color: 'var(--gold)', direction: 'rtl', lineHeight: 1.2, marginBottom: '0.25rem' }}>{q.word}</div>
        <div style={{ fontSize: '0.72rem', color: 'var(--text3)', fontStyle: 'italic' }}>{q.meaning}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.45rem', marginBottom: '0.85rem' }}>
        {q.options.map(opt => {
          let bg = 'var(--bg3)', border = 'var(--border)', color = 'var(--text)';
          if (done) { if (opt === q.correct) { bg = 'rgba(52,211,153,0.12)'; border = 'rgba(52,211,153,0.5)'; color = '#34d399'; } else if (opt === chosen) { bg = 'rgba(248,113,113,0.1)'; border = 'rgba(248,113,113,0.45)'; color = '#f87171'; } }
          return <button key={opt} onClick={() => pick(opt)} style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.05rem', direction: 'rtl', padding: '0.6rem 0.5rem', background: bg, border: `1px solid ${border}`, borderRadius: 5, color, cursor: done ? 'default' : 'pointer', transition: 'all 0.15s', textAlign: 'center', minHeight: 44 }}>{opt}</button>;
        })}
      </div>
      {done && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: '0.8rem', color: chosen === q.correct ? '#34d399' : '#f87171', fontStyle: 'italic' }}>{chosen === q.correct ? 'Correct!' : `Answer: ${q.correct}`}</div>
        <button onClick={next} className="pill-btn" style={{ fontSize: '0.6rem' }}>{qIdx + 1 < filtered.length ? 'Next →' : 'Restart ↺'}</button>
      </div>}
    </div>
  );
}

// ── Drills panel (tab-switcher wrapping all drill exercises) ──────────────────
function DbDrillsPanel({ analysis, verseWords, displayVerses, verseIdx }) {
  const [drill, setDrill] = useState('root');
  const drills = [
    ['root',   'Root Drill'],
    ['match',  'Word Match'],
    ['wazn',   'Pattern وزن'],
    ['pos',    'POS Sort'],
    ['rsort',  'Root Sort'],
    ['sadr',   'Ṣadr/ʿAjz'],
    ['broken', 'Broken ج'],
    ['syn',    'Synonym'],
  ];
  return (
    <div>
      <div style={{ display: 'flex', gap: '0.35rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {drills.map(([key, label]) => (
          <button key={key} onClick={() => setDrill(key)} style={{
            fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.48rem',
            letterSpacing: '0.08em', textTransform: 'uppercase',
            padding: '0.25rem 0.65rem', borderRadius: 20,
            background: drill === key ? 'rgba(229,150,83,0.16)' : 'var(--bg3)',
            border: `1px solid ${drill === key ? 'rgba(229,150,83,0.6)' : 'var(--border)'}`,
            color: drill === key ? 'var(--gold)' : 'var(--text3)',
            cursor: 'pointer',
          }}>{label}</button>
        ))}
      </div>
      {drill === 'root'   && <DbRootDrill   analysis={analysis} />}
      {drill === 'match'  && <DbMatchGame   analysis={analysis} />}
      {drill === 'wazn'   && <DbWaznDrill   analysis={analysis} />}
      {drill === 'pos'    && <DbPOSSort     verseWords={verseWords} analysis={analysis} />}
      {drill === 'rsort'  && <DbRootSort    analysis={analysis} />}
      {drill === 'sadr'   && <DbSadrAjzPair displayVerses={displayVerses} verseIdx={verseIdx} />}
      {drill === 'broken' && <DbBrokenPluralSpotter analysis={analysis} />}
      {drill === 'syn'    && <DbSynonymDrill analysis={analysis} />}
    </div>
  );
}

function DbLiteraryDevices({ analysis }) {
  const devices = analysis?.literaryDevices || [];
  if (!devices.length) return (
    <div style={{
      color: 'var(--text3)',
      fontStyle: 'italic',
      fontSize: '0.82rem',
      textAlign: 'center',
      padding: '0.5rem 0',
      lineHeight: 1.6,
    }}>
      No rhetorical devices noted for this verse.
    </div>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {devices.map((d, i) => (
        <div key={i} style={{ background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 4, padding: '0.9rem 1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
            <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.1rem', color: 'var(--gold)', direction: 'rtl' }}>{d.nameAr}</div>
            <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--em)', background: 'rgba(140,207,225,0.1)', border: '1px solid rgba(140,207,225,0.22)', borderRadius: 3, padding: '0.2rem 0.45rem' }}>{d.nameEn || d.type}</div>
          </div>
          <div style={{ fontSize: '0.84rem', color: 'var(--text2)', lineHeight: 1.6, marginBottom: d.highlightAr ? '0.5rem' : 0 }}>{d.explanation}</div>
          {d.highlightAr && (
            <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.9rem', direction: 'rtl', textAlign: 'right', color: 'var(--gold2)', fontStyle: 'italic', background: 'rgba(229,150,83,0.06)', border: '1px solid rgba(229,150,83,0.11)', borderRadius: 3, padding: '0.35rem 0.6rem' }}>{d.highlightAr}</div>
          )}
        </div>
      ))}
    </div>
  );
}


// ── Collapsible study tool panel ─────────────────────────────────────────────
function StudyTool({ icon, name, meta, isOpen, onToggle, children }) {
  return (
    <div style={{ marginBottom: '0.65rem', border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden' }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '0.65rem',
          padding: '0.85rem 1rem',
          background: isOpen ? 'rgba(229,150,83,0.07)' : 'var(--bg2)',
          border: 'none', borderBottom: isOpen ? '1px solid var(--border)' : 'none',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ fontSize: '1.05rem' }}>{icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.82rem', color: 'var(--text)', fontWeight: 600 }}>{name}</div>
          <div style={{
            fontFamily: "'Josefin Sans', sans-serif",
            fontSize: '0.55rem', letterSpacing: '0.08em',
            textTransform: 'uppercase', color: 'var(--text3)', marginTop: '0.1rem',
          }}>{meta}</div>
        </div>
        <span style={{
          color: 'var(--gold)', fontSize: '0.7rem',
          transition: 'transform 0.2s', display: 'inline-block',
          transform: isOpen ? 'rotate(180deg)' : 'none',
        }}>▾</span>
      </button>
      {isOpen && (
        <div style={{ padding: '1rem', background: 'var(--bg)' }}>
          {children}
        </div>
      )}
    </div>
  );
}


// ── Verse depth strip ─────────────────────────────────────────────────────────
// Replaces the plain dot navigation with depth-coloured dots.
// Depths: unvisited (empty ring) → read (dim) → study (gold) → ai (teal)
const DEPTH_STYLE = {
  unvisited: { bg: 'transparent',              border: 'var(--border)',  size: 6 },
  read:      { bg: 'rgba(140,207,225,0.22)',    border: 'transparent',   size: 6 },
  study:     { bg: 'var(--gold)',               border: 'transparent',   size: 8 },
  ai:        { bg: 'var(--em)',                 border: 'transparent',   size: 8 },
};

function VerseDepthStrip({ total, depths, current, onNavigate }) {
  if (total > 30) {
    // For long poems show scrollable mini-strip + text counter
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem', maxWidth: '55%' }}>
        <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.62rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text3)' }}>
          Bayt {current + 1} / {total}
        </span>
        <div style={{ display: 'flex', gap: '0.2rem', overflowX: 'auto', maxWidth: '100%', paddingBottom: 2, scrollbarWidth: 'none' }}>
          {Array.from({ length: total }, (_, i) => {
            const d = depths[i] ?? 'unvisited';
            const s = DEPTH_STYLE[d];
            const isCur = i === current;
            return (
              <div key={i} onClick={() => onNavigate(i)} style={{ width: isCur ? 7 : s.size, height: isCur ? 7 : s.size, borderRadius: '50%', background: isCur ? 'var(--em)' : s.bg, border: `1px solid ${isCur ? 'var(--em)' : s.border}`, cursor: 'pointer', flexShrink: 0, outline: isCur ? '2px solid var(--em)' : 'none', outlineOffset: 1, transition: 'background 0.2s' }} />
            );
          })}
        </div>
        <DepthLegend depths={depths} total={total} />
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem', maxWidth: '60%' }}>
      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', justifyContent: 'center' }}>
        {Array.from({ length: total }, (_, i) => {
          const d = depths[i] ?? 'unvisited';
          const s = DEPTH_STYLE[d];
          const isCur = i === current;
          return (
            <div key={i} onClick={() => onNavigate(i)} title={`Bayt ${i + 1}${d !== 'unvisited' ? ` · ${d}` : ''}`} style={{ width: isCur ? 8 : s.size, height: isCur ? 8 : s.size, borderRadius: '50%', background: isCur ? 'var(--em)' : s.bg, border: `1px solid ${isCur ? 'var(--em)' : s.border}`, cursor: 'pointer', outline: isCur ? '2px solid rgba(140,207,225,0.35)' : 'none', outlineOffset: 1, transition: 'background 0.2s, width 0.15s, height 0.15s', touchAction: 'manipulation' }} />
          );
        })}
      </div>
      <DepthLegend depths={depths} total={total} />
    </div>
  );
}

function DepthLegend({ depths, total }) {
  const vals = Object.values(depths);
  const hasStudy = vals.includes('study') || vals.includes('ai');
  const hasAI    = vals.includes('ai');
  const readCnt  = vals.length;
  if (!readCnt) return null;
  return (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.48rem', letterSpacing: '0.08em', color: 'var(--text3)', textTransform: 'uppercase' }}>
      <span>{readCnt}/{total} read</span>
      {hasStudy && <span style={{ color: 'var(--gold)' }}>· {vals.filter(v => v === 'study' || v === 'ai').length} studied</span>}
      {hasAI    && <span style={{ color: 'var(--em)'  }}>· {vals.filter(v => v === 'ai').length} AI-explored</span>}
    </div>
  );
}

// ── DB poem reader (for poems loaded from the database) ───────────────────────
function DbPoem({ poemId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [userId, setUserId] = useState(() => lsGet('diwanPrismaUserId'));
  const tokenRef = useRef(null);
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      tokenRef.current = session?.access_token ?? null;
    });
    // Re-read userId if AuthContext re-hydrates it after mount (Android storage wipe recovery)
    const onStorage = (e) => {
      if (e.key === 'diwanPrismaUserId' && e.newValue) setUserId(e.newValue);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  const { depths, markRead, markStudy, markAI, markManual, unmarkStudy, backfillFromSession } = useVerseDepth(poemId);

  const [poem, setPoem]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [verseIdx, setVerseIdx]   = useState(0);
  const [activeTab, setActiveTab] = useState('Read');
  const [transRevealed, setTransRevealed] = useState(() => lsGet('diwanTransPref') === 'show');
  const [alwaysShowTrans, setAlwaysShowTrans] = useState(() => lsGet('diwanTransPref') === 'show');
  const [studyHintSeen, setStudyHintSeen] = useState(false);
  const [showFull, setShowFull]   = useState(false);
  const [lang, setLang]           = useState('ar');
  const [ctxOpen, setCtxOpen]     = useState(false);
  const [ctxTab, setCtxTab]       = useState('Poet');
  const [ctxSize, setCtxSize]     = useState(() => localStorage.getItem('diwanCtxSize') || 'md');

  const CTX_SIZES = { sm: 0.82, md: 1, lg: 1.38 };
  function cycleCtxSize(dir, e) {
    e.stopPropagation();
    const steps = ['sm', 'md', 'lg'];
    const next = steps[Math.max(0, Math.min(2, steps.indexOf(ctxSize) + dir))];
    setCtxSize(next);
    localStorage.setItem('diwanCtxSize', next);
    if (!ctxOpen) setCtxOpen(true);
  }
  const [ctxData, setCtxData]     = useState(null);
  const [ctxLoading, setCtxLoading] = useState(false);
  const [ctxError, setCtxError]   = useState(false);
  const ctxFetched = useRef(false);
  const [contemp, setContemp]         = useState(null);
  const [contempLoading, setContempLoading] = useState(false);
  const [contempError, setContempError]     = useState(null);
  const { balance: walletBalance, loading: walletLoading } = useWallet(userId);
  const analysisRetryRef    = useRef(0);
  const quickTransRetryRef  = useRef({});
  const [verseAnalyses, setVerseAnalyses] = useState({});
  const [verseLoading, setVerseLoading]   = useState(false);
  const [quickTrans, setQuickTrans]       = useState({});
  const [transCountdown, setTransCountdown] = useState(null);
  const [openTool, setOpenTool]           = useState(null);
  const [tappedWord, setTappedWord]       = useState(null);
  const [helpOpen, setHelpOpen]           = useState(false);
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  const tabBarRef      = useRef(null);
  const verseAreaRef   = useRef(null);
  const navRef         = useRef(null);
  const studySectionRef = useRef(null);
  const refSectionRef   = useRef(null);
  const ctxRef          = useRef(null);
  const fontSizeRef     = useRef(null);
  const askAiRef        = useRef(null);
  const memoryRowRef    = useRef(null);
  const [analysisFailed, setAnalysisFailed] = useState(false);
  const [noBalance, setNoBalance]           = useState(false);
  const [slowLoad, setSlowLoad]             = useState(false);
  const [, setAnalysisErrCode] = useState(null);
  const [analysisRetryCount, setAnalysisRetryCount] = useState(0);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [showTopUpFromPoem, setShowTopUpFromPoem] = useState(false);
  const [plainReadingAr, setPlainReadingAr]         = useState(true);
  const [plainReadingEnCache, setPlainReadingEnCache] = useState({});
  const [plainReadingEnLoading, setPlainReadingEnLoading] = useState(false);
  const [bioExpanded, setBioExpanded]       = useState(false);
  const [vocabLang, setVocabLang]          = useState('ar');
  const [savedWords, setSavedWords]       = useState([]);
  const [optimisticSaved, setOptimisticSaved] = useState(new Set());
  const [bookmarked, setBookmarked]       = useState(false);
  const [memoryStatus, setMemoryStatus]   = useState(null); // STUDYING | MEMORISING | MEMORISED | null

  const [verseCopied, setVerseCopied]     = useState(false);
  const [shareOpen, setShareOpen]         = useState(false);
  const [searchParams] = useSearchParams();

  // Type-B detection: scraper stored titleAr as H1 but verses start at H2.
  // Re-group all hemistichs into correct bayt pairs before rendering/translating.
  const displayVerses = useMemo(() => {
    const rawVerses = poem?.verses || [];
    if (!poem?.titleAr || !rawVerses.length) return rawVerses;
    const firstH = (rawVerses[0].textAr || '').split(' / ')[0].trim();
    if (stripHarakat(firstH) === stripHarakat(poem.titleAr.trim())) return rawVerses;
    const allH = [poem.titleAr.trim()];
    rawVerses.forEach(v => {
      (v.textAr || '').split(' / ').forEach(h => { const t = h.trim(); if (t) allH.push(t); });
    });
    const rebuilt = [];
    for (let i = 0; i + 1 < allH.length; i += 2) rebuilt.push({ textAr: allH[i] + ' / ' + allH[i + 1] });
    if (allH.length % 2 !== 0) rebuilt.push({ textAr: allH[allH.length - 1] });
    return rebuilt;
  }, [poem]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSavedWords = useCallback(() => {
    if (!userId || !poemId) return;
    fetch(`${API}/api/users/${userId}/words?poemId=${poemId}`, {
      headers: userReadHeaders(userId, tokenRef.current),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (Array.isArray(data)) setSavedWords(data); })
      .catch(() => {});
  }, [userId, poemId]);

  useEffect(() => { loadSavedWords(); }, [loadSavedWords]);

  // Reset study hint every time the user moves to a new bayt
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setStudyHintSeen(false); }, [verseIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load bookmark status for this poem
  useEffect(() => {
    if (!userId || !poemId) return;
    fetch(`${API}/api/users/${userId}/bookmarks?poemId=${poemId}`, {
      headers: userReadHeaders(userId, tokenRef.current),
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setBookmarked(true);
          setMemoryStatus(data[0].memoryStatus ?? null);
        } else {
          setBookmarked(false);
          setMemoryStatus(null);
        }
      })
      .catch(() => {});
  }, [userId, poemId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function toggleBookmark() {
    if (!userId || !poemId) return;
    const next = !bookmarked;
    setBookmarked(next);
    if (!next) setMemoryStatus(null);
    try {
      await fetch(`${API}/api/users/${userId}/bookmarks`, {
        method: next ? 'POST' : 'DELETE',
        headers: userWriteHeaders(userId, tokenRef.current),
        body: JSON.stringify({ poemId }),
      });
    } catch {
      setBookmarked(!next); // revert on error
    }
  }

  async function handleMemoryStatus(status) {
    if (!userId || !poemId) return;
    const next = memoryStatus === status ? null : status; // tap active = clear
    setMemoryStatus(next);
    if (next && !bookmarked) setBookmarked(true); // auto-bookmark
    try {
      await fetch(`${API}/api/users/${userId}/bookmarks`, {
        method: 'PATCH',
        headers: userWriteHeaders(userId, tokenRef.current),
        body: JSON.stringify({ poemId, memoryStatus: next }),
      });
    } catch {
      setMemoryStatus(memoryStatus); // revert on error
    }
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!poemId) { setLoading(false); return; }
    // Reset every piece of poem-specific state before loading the new poem.
    // Without this, navigating A → B shows stale content, wrong verse index,
    // and the context panel silently skips fetching (ctxFetched stays true).
    setPoem(null);
    setLoading(true);
    setFetchError(false);
    setVerseIdx(0);
    setActiveTab('Read');
    setTransRevealed(lsGet('diwanTransPref') === 'show');
    setShowFull(false);
    setCtxOpen(false);
    setCtxData(null);
    setCtxLoading(false);
    setCtxError(false);
    ctxFetched.current = false;
    setVerseAnalyses({});
    setQuickTrans({});
    setTappedWord(null);
    setBioExpanded(false);
    setOpenTool(null);
    setAnalysisFailed(false);
    setNoBalance(false);
    setAnalysisErrCode(null);
    quickTransRetryRef.current = {};

    const ctrl = new AbortController();
    fetch(`${API}/api/poems/${poemId}`, { signal: ctrl.signal })
      .then(r => {
        if (r.status === 404) { setLoading(false); return null; }
        if (!r.ok) throw new Error('non-ok');
        return r.json();
      })
      .then(data => {
        if (data !== null) {
          setPoem(data ?? null);
          // ?v=N (1-indexed) from share link takes priority over history-state resume
          const vParam = parseInt(searchParams.get('v'), 10);
          if (Number.isFinite(vParam) && vParam >= 1) {
            setVerseIdx(vParam - 1);
          } else {
            const resume = location.state?.resumeVerse;
            if (resume && Number.isInteger(resume) && resume > 0) setVerseIdx(resume);
          }
          setLoading(false);
          trackEvent('poem_opened', { poemId, poetAr: data?.poetAr, topic: data?.topic });
        }
      })
      .catch(err => { if (err.name !== 'AbortError') { setFetchError(true); setLoading(false); } });
    return () => ctrl.abort();
  }, [poemId]);

  // Quick translation for the Read tab — fires on every verse change, fast (Gemini / cached)
  useEffect(() => {
    if (!poem || quickTrans[verseIdx]) return;
    const verse = displayVerses[verseIdx];
    if (!verse) return;
    const ctrl = new AbortController();
    const idx = verseIdx;
    analysisPost(`${API}/api/analysis/quick-translate`, { verseAr: verse.textAr, poetAr: poem.poetAr }, ctrl.signal)
      .then(r => {
        if (!r.ok) {
          const resetMs = r.status === 429 ? Number(r.headers.get('X-RateLimit-Reset') || 0) : 0;
          return r.json()
            .then(d => { const err = new Error(`API_${r.status}_${d.code || 'ERR'}`); if (resetMs) err.resetMs = resetMs; return Promise.reject(err); })
            .catch(e => { if (e.message.startsWith('API_')) return Promise.reject(e); const err = new Error(`API_${r.status}`); if (resetMs) err.resetMs = resetMs; return Promise.reject(err); });
        }
        return r.json();
      })
      .then(data => { quickTransRetryRef.current[idx] = 0; setQuickTrans(prev => ({ ...prev, [idx]: data })); })
      .catch(err => {
        if (err.name === 'AbortError') return;
        const is429 = err.message.includes('429');
        if (is429 && (quickTransRetryRef.current[idx] || 0) < 1) {
          quickTransRetryRef.current[idx] = (quickTransRetryRef.current[idx] || 0) + 1;
          setQuickTrans(prev => ({ ...prev, [idx]: { translationEn: null, _error: true, _retrying: true } }));
          let secs = err.resetMs ? Math.max(1, Math.ceil((err.resetMs - Date.now()) / 1000)) : 60;
          setTransCountdown(secs);
          const tick = setInterval(() => {
            secs -= 1;
            if (secs <= 0) {
              clearInterval(tick);
              setTransCountdown(null);
              setQuickTrans(prev => { const n = { ...prev }; delete n[idx]; return n; });
            } else {
              setTransCountdown(secs);
            }
          }, 1000);
        } else {
          setQuickTrans(prev => ({ ...prev, [idx]: { translationEn: null, _error: true, _is429: is429 } }));
        }
      });
    return () => ctrl.abort();
  }, [poem, verseIdx]);

  // Auto-translate explanationAr → EN when EN tab is selected and explanationEn not yet cached
  useEffect(() => {
    const ar = verseAnalyses[verseIdx];
    if (plainReadingAr || !poem || !ar?.explanationAr || ar?.explanationEn || plainReadingEnCache[verseIdx]) return;
    const ctrl = new AbortController();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPlainReadingEnLoading(true);
    analysisPost(`${API}/api/analysis/quick-translate`, { verseAr: ar.explanationAr, poetAr: poem.poetAr }, ctrl.signal)
      .then(r => r.ok ? r.json() : Promise.reject(new Error('non-ok')))
      .then(data => { if (data?.translationEn) setPlainReadingEnCache(prev => ({ ...prev, [verseIdx]: data.translationEn })); })
      .catch(err => { if (err.name !== 'AbortError') console.warn('[diwan] plain reading EN translate failed', err.message); })
      .finally(() => setPlainReadingEnLoading(false));
    return () => ctrl.abort();
  }, [plainReadingAr, verseIdx, verseAnalyses, poem]);

  // Full verse analysis — fetched eagerly so READ tab tappable words have vocabulary data
  useEffect(() => {
    if (!poem) return;
    // Clear any error state from the previous verse / previous attempt before starting fresh
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setNoBalance(false);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAnalysisFailed(false);
    if (verseAnalyses[verseIdx]) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setVerseLoading(false);
      return;
    }
    const verse = displayVerses[verseIdx];
    if (!verse) return;
    const ctrl = new AbortController();
    const idx = verseIdx;
    analysisRetryRef.current = 0;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setVerseLoading(true);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSlowLoad(false);
    const slowTimer = setTimeout(() => setSlowLoad(true), 3000);

    async function doFetch() {
      // Always get a fresh token — tokenRef.current may be null on first render
      // if the getSession() call above hasn't resolved yet.
      let freshToken = tokenRef.current;
      if (!freshToken) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          freshToken = session?.access_token ?? null;
          tokenRef.current = freshToken;
        } catch (_) { /* best effort */ }
      }
      analysisPost(`${API}/api/analysis/verse`, { verseAr: verse.textAr, poemTitleAr: poem.titleAr, poetAr: poem.poetAr, era: poem.era }, ctrl.signal, { token: freshToken, userId })
        .then(r => {
          if (!r.ok) {
            return r.json()
              .then(d => Promise.reject(new Error(`API_${r.status}_${d.code || 'ERR'}`)))
              .catch(e => { if (e.message.startsWith('API_')) return Promise.reject(e); return Promise.reject(new Error(`API_${r.status}`)); });
          }
          return r.json();
        })
        .then(data => { setVerseAnalyses(prev => ({ ...prev, [idx]: data })); setVerseLoading(false); setSlowLoad(false); setAnalysisErrCode(null); })
        .catch(err => {
          if (err.name === 'AbortError') return;
          // 402 = wallet empty — show top-up prompt immediately, no retry
          if (err.message?.includes('API_402')) {
            setVerseLoading(false);
            setSlowLoad(false);
            setNoBalance(true);
            return;
          }
          if (analysisRetryRef.current < 1) {
            analysisRetryRef.current++;
            setTimeout(doFetch, 2000);
          } else {
            setVerseLoading(false);
            setSlowLoad(false);
            setAnalysisFailed(true);
            setAnalysisErrCode(err.message);
            // Log silently — never shown to the user
            captureException(err, `analyseVerse failed: ${err.message}`);
          }
        });
    }
    doFetch();
    return () => { ctrl.abort(); clearTimeout(slowTimer); setSlowLoad(false); };
  }, [poem, verseIdx, analysisRetryCount]);

  // Prefetch the next verse's analysis in the background so navigating
  // forward feels instant after the first verse loads.
  useEffect(() => {
    if (!poem || !verseAnalyses[verseIdx]) return; // only prefetch once current verse is done
    const nextIdx = verseIdx + 1;
    const nextVerse = displayVerses[nextIdx];
    if (!nextVerse || verseAnalyses[nextIdx]) return;
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      analysisPost(`${API}/api/analysis/verse`, {
        verseAr: nextVerse.textAr,
        poemTitleAr: poem.titleAr,
        poetAr: poem.poetAr,
        era: poem.era,
      }, ctrl.signal, { token: tokenRef.current, userId })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) setVerseAnalyses(prev => prev[nextIdx] ? prev : { ...prev, [nextIdx]: data });
        })
        .catch(() => {}); // silent — best-effort prefetch
    }, 400);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [verseAnalyses, verseIdx, poem]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefetch the next verse's quick translation so the Read tab is instant on navigation.
  useEffect(() => {
    if (!poem || !quickTrans[verseIdx]) return;
    const nextIdx = verseIdx + 1;
    const nextVerse = displayVerses[nextIdx];
    if (!nextVerse || quickTrans[nextIdx]) return;
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      analysisPost(`${API}/api/analysis/quick-translate`, {
        verseAr: nextVerse.textAr,
        poetAr: poem.poetAr,
      }, ctrl.signal)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) setQuickTrans(prev => prev[nextIdx] ? prev : { ...prev, [nextIdx]: data });
        })
        .catch(() => {}); // silent — best-effort prefetch
    }, 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [quickTrans, verseIdx, poem]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mark poem as READING when it first loads (fire-and-forget)
  useEffect(() => {
    if (!poem || !userId || !poemId) return;
    fetch(`${API}/api/users/${userId}/progress`, {
      method: 'POST',
      headers: userWriteHeaders(userId, tokenRef.current),
      body: JSON.stringify({ poemId, status: 'READING', currentVerse: 0 }),
    }).catch(() => {});
  }, [poem?.id, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Upgrade to STUDIED when the user reaches the final verse
  useEffect(() => {
    const totalVerses = poem?.verses?.length ?? 0;
    if (!poem || !userId || !poemId || !totalVerses) return;
    if (verseIdx < totalVerses - 1) return;
    fetch(`${API}/api/users/${userId}/progress`, {
      method: 'POST',
      headers: userWriteHeaders(userId, tokenRef.current),
      body: JSON.stringify({ poemId, status: 'STUDIED', currentVerse: verseIdx }),
    }).catch(() => {});
  }, [verseIdx, poem?.id, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced save of currentVerse as user navigates bayts (powers fractional bar fill)
  useEffect(() => {
    const totalVerses = poem?.verses?.length ?? 0;
    if (!poem || !userId || !poemId || !totalVerses) return;
    if (verseIdx === 0 || verseIdx >= totalVerses - 1) return; // handled by READING/STUDIED effects
    const timer = setTimeout(() => {
      fetch(`${API}/api/users/${userId}/progress`, {
        method: 'POST',
        headers: userWriteHeaders(userId, tokenRef.current),
        body: JSON.stringify({ poemId, status: 'READING', currentVerse: verseIdx }),
      }).catch(() => {});
    }, 3000);
    return () => clearTimeout(timer);
  }, [verseIdx, poem?.id, userId, poemId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Per-verse depth tracking (localStorage) ─────────────────────────────
  // Auto-mark current verse as 'read' whenever verseIdx changes
  useEffect(() => { if (poem) markRead(verseIdx); }, [verseIdx, poem?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // Auto-mark as 'study' when Study tab opens
  useEffect(() => { if (activeTab === 'Study') markStudy(verseIdx); }, [activeTab, verseIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save reading position for guest users (no login required)
  // For logged-in users the server already tracks this via the progress POST above
  useEffect(() => {
    if (!poem || userId) return; // logged-in users use server progress
    const totalVerses = poem?.verses?.length ?? 0;
    if (!totalVerses) return;
    guestSaveProgress(
      poemId,
      poem.titleAr,
      poem.poetAr,
      verseIdx,
      totalVerses,
    );
  }, [verseIdx, poem?.id, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist last-opened poem so Words/Saved can show a "Back to poem" breadcrumb
  useEffect(() => {
    if (!poem) return;
    lsSet('diwanLastPoem', JSON.stringify({ poemId, titleAr: poem.titleAr, poetAr: poem.poetAr, poetEn: poem.poetEn }));
  }, [poem?.id, poemId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-fetch poem context (meaning, reception, theme history) as soon as poem loads.
  // Moved from openCtx() so panels are never blank on first open.
  useEffect(() => {
    if (!poem || ctxFetched.current) return;
    ctxFetched.current = true;
    setCtxLoading(true);
    const ctrl = new AbortController();
    const firstVerses = displayVerses.slice(0, 3).map(v => v.textAr).join('\n');
    analysisPost(
      `${API}/api/analysis/context`,
      { titleAr: poem.titleAr, poetAr: poem.poetAr, era: poem.era, firstVerses, poemId },
      ctrl.signal,
    )
      .then(r => { if (!r.ok) throw new Error(`API ${r.status}`); return r.json(); })
      .then(data => { setCtxData(data); setCtxLoading(false); })
      .catch(err => {
        if (err.name !== 'AbortError') {
          setCtxLoading(false);
          setCtxError(true);
          console.warn('[diwan] poemContext failed', err);
        }
      });
    return () => ctrl.abort();
  }, [poem?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function openCtx() {
    setCtxOpen(o => !o); // fetch now handled by useEffect above
  }

  // Lazy-fetch contemporaries when that tab is first opened
  useEffect(() => {
    if (ctxTab !== 'Contemporaries' || !poem?.poet?.id || contemp || contempLoading) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setContempLoading(true);
    analysisPost(
      `${API}/api/analysis/contemporaries`,
      { poetId: poem.poet.id, nameAr: poem.poetAr, nameEn: poem.poet.nameEn, era: poem.era },
    )
      .then(r => { if (!r.ok) throw new Error(`API ${r.status}`); return r.json(); })
      .then(data => { setContemp(data); setContempLoading(false); })
      .catch(err => { setContempError(err.message); setContempLoading(false); });
  }, [ctxTab, poem?.poet?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Must stay above early returns — hooks cannot be called conditionally
  const verses     = poem?.verses || [];
  const verse      = displayVerses[verseIdx];
  const verseWords = useMemo(() => verse?.textAr ? splitVerseWords(verse.textAr) : [], [verse]);
  const analysis   = verseAnalyses[verseIdx];

  if (loading) {
    return (
      <div className="phone-shell">
        <div className="accent-bar" />
        <div style={{ padding: '2.75rem 1.5rem 1.25rem' }}>
          <div className="skeleton" style={{ height: 18, width: 60, marginBottom: 12 }} />
          <div className="skeleton" style={{ height: 14, width: '45%', marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 20, width: '70%', marginBottom: 12 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <div className="skeleton" style={{ height: 18, width: 48, borderRadius: 20 }} />
            <div className="skeleton" style={{ height: 18, width: 64, borderRadius: 20 }} />
          </div>
        </div>
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
          <div className="skeleton" style={{ flex: 1, height: 36 }} />
          <div className="skeleton" style={{ flex: 1, height: 36 }} />
          <div className="skeleton" style={{ flex: 1, height: 36 }} />
        </div>
        <div style={{ padding: '1.5rem' }}>
          <div className="skeleton" style={{ height: 10, width: '30%', margin: '0 auto', marginBottom: '1.25rem' }} />
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, padding: '1.75rem 1.5rem' }}>
            <div className="skeleton" style={{ height: 16, width: '70%', marginBottom: 20 }} />
            <div className="skeleton" style={{ height: 16, width: '90%', marginBottom: 20 }} />
            <div className="skeleton" style={{ height: 16, width: '55%', marginBottom: 20 }} />
            <div className="skeleton" style={{ height: 16, width: '80%', marginBottom: 20 }} />
          </div>
          <div className="skeleton" style={{ height: 48, borderRadius: 6, width: '100%', marginTop: 16, marginBottom: 16 }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="skeleton" style={{ width: 36, height: 36, borderRadius: 4 }} />
            <div style={{ display: 'flex', gap: 6 }}>
              <div className="skeleton" style={{ width: 6, height: 6, borderRadius: '50%' }} />
              <div className="skeleton" style={{ width: 6, height: 6, borderRadius: '50%' }} />
              <div className="skeleton" style={{ width: 6, height: 6, borderRadius: '50%' }} />
              <div className="skeleton" style={{ width: 6, height: 6, borderRadius: '50%' }} />
              <div className="skeleton" style={{ width: 6, height: 6, borderRadius: '50%' }} />
            </div>
            <div className="skeleton" style={{ width: 36, height: 36, borderRadius: 4 }} />
          </div>
        </div>
        <ArabicLoader message="Unrolling the scroll…" />
        <BottomNav />
      </div>
    );
  }

  if (!poem || poem.error) {
    return (
      <div className="phone-shell">
        <div className="accent-bar" />
        <div className="screen-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem 1.5rem' }}>
          <div style={{ textAlign: 'center' }}>
            <div role="alert" style={{ color: 'var(--text3)', fontStyle: 'italic', marginBottom: '1rem' }}>
              {fetchError ? 'The Dīwān is out of reach — check your connection.' : 'This poem seems to have wandered off.'}
            </div>
            <button className="back-btn" onClick={() => navigate(-1)}>← Back</button>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  const CTX_TABS  = ['Poet', 'Contemporaries', 'Theme', 'Interpretations', 'Reception'];

  function changeVerse(dir) {
    const next = verseIdx + dir;
    if (next < 0 || next >= displayVerses.length) return;
    setVerseIdx(next);
    setTappedWord(null);
    setAnalysisFailed(false);
    setNoBalance(false);
    setAnalysisErrCode(null);
  }

  function saveWord(vocabEntry) {
    // Optimistic: mark green tick immediately
    setOptimisticSaved(prev => new Set([...prev, vocabEntry.wordAr]));
    // Optimistic: add to Reference tab immediately (deduped by wordAr)
    setSavedWords(prev => {
      if (prev.some(w => w.wordAr === vocabEntry.wordAr)) return prev;
      return [{ id: `opt-${Date.now()}`, wordAr: vocabEntry.wordAr, romanisation: vocabEntry.romanisation, meaningEn: vocabEntry.meaningEn, rootLetters: vocabEntry.rootLetters, definitionAr: vocabEntry.definitionAr, savedAt: new Date().toISOString() }, ...prev];
    });
    if (userId && poemId) {
      fetch(`${API}/api/users/${userId}/words`, {
        method: 'POST',
        headers: userWriteHeaders(userId, tokenRef.current),
        body: JSON.stringify({
          wordAr: vocabEntry.wordAr,
          romanisation: vocabEntry.romanisation,
          definitionAr: vocabEntry.definitionAr,
          meaningEn: vocabEntry.meaningEn,
          rootLetters: vocabEntry.rootLetters,
          poemId,
        }),
      })
      .then(r => { if (r.ok) loadSavedWords(); })
      .catch(() => {});
    }
    // Popup stays open — caller sees ✓ Saved state instead
  }


  return (
    <div className="phone-shell">
      <div className="accent-bar" />
      <div className="screen-content">

        {/* Header */}
        <div style={{ padding: '2.75rem 1.5rem 1.25rem', borderBottom: '1px solid var(--border)', position: 'relative', zIndex: 1 }}>
          <div style={{ marginBottom: '0.5rem' }}>
            <button className="back-btn" style={{ margin: 0 }} onClick={() => navigate(-1)}>← Back</button>
          </div>
          {poem.poetAr && (() => {
            const enName = poem.poetEn;
            return (
              <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '0.3rem', opacity: 0.7, marginBottom: '0.2rem' }}>
                {enName && (
                  <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--em)' }}>
                    {enName}
                  </span>
                )}
                {enName && (
                  <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.6rem', color: 'var(--em)' }}>·</span>
                )}
                <span style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.82rem', color: 'var(--em)', direction: 'rtl' }}>
                  {poem.poetAr}
                </span>
              </div>
            );
          })()}
          {poem.titleEn ? (
            <div
              style={{
                fontSize: '1.1rem', color: 'var(--text)', fontStyle: 'italic', marginBottom: '0.4rem',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
              }}
              title={poem.titleEn}
            >
              {poem.titleEn}
            </div>
          ) : poem.verseCount ? (
            <div style={{ fontSize: '0.7rem', color: 'var(--text3)', marginBottom: '0.4rem', letterSpacing: '0.08em', fontFamily: "'Josefin Sans', sans-serif", textTransform: 'uppercase' }}>
              {poem.verseCount} {poem.verseCount === 1 ? 'bayt' : 'bayts'}
            </div>
          ) : null}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
              {poem.era   && <span className="tag tag-gold">{poem.era}</span>}
              {poem.topic && <span className="tag tag-em">{poem.topic}</span>}
              {poem.meter && <span className="tag">{poem.meter}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
              <button
                className="question-btn"
                onClick={() => setSpotlightOpen(true)}
                aria-label="Help"
              >?</button>
              {userId && (
                <button
                  onClick={toggleBookmark}
                  aria-label={bookmarked ? 'Remove bookmark' : 'Bookmark poem'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1, padding: '0.1rem 0.2rem', transition: 'opacity 0.15s', flexShrink: 0 }}
                >
                  <svg width="14" height="17" viewBox="0 0 16 20" fill={bookmarked ? 'var(--gold)' : 'none'} stroke={bookmarked ? 'var(--gold)' : 'var(--text3)'} strokeWidth="1.5" strokeLinejoin="round">
                    <path d="M2 2h12v16l-6-4-6 4V2z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Memory status row */}
        {userId && (
          <div ref={memoryRowRef} style={{ padding: '0.5rem 1.5rem', borderBottom: '1px solid var(--border2)', display: 'flex', alignItems: 'center', gap: '0.4rem', overflowX: 'auto', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
            <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.48rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text3)', opacity: 0.7, marginRight: '0.2rem' }}>
              My progress
            </span>
            {[
              { key: 'STUDYING',   label: 'Studying'  },
              { key: 'MEMORISING', label: 'Memorising' },
              { key: 'MEMORISED',  label: 'Memorised'  },
              { key: 'FAVOURITE',  label: '♥ Favourite' },
            ].map(({ key, label }) => {
              const active = memoryStatus === key;
              const color  = key === 'MEMORISED' ? 'var(--em)' : key === 'MEMORISING' ? 'var(--gold)' : key === 'FAVOURITE' ? '#f9a8d4' : 'var(--text2)';
              const bg     = key === 'MEMORISED' ? 'rgba(140,207,225,0.1)' : key === 'MEMORISING' ? 'rgba(212,168,83,0.1)' : key === 'FAVOURITE' ? 'rgba(249,168,212,0.1)' : 'var(--bg3)';
              return (
                <button
                  key={key}
                  onClick={() => handleMemoryStatus(key)}
                  style={{
                    background: active ? bg : 'none',
                    border: `1px solid ${active ? color : 'var(--border)'}`,
                    color: active ? color : 'var(--text3)',
                    fontFamily: "'Josefin Sans', sans-serif",
                    fontSize: '0.5rem', letterSpacing: '0.1em', textTransform: 'uppercase',
                    borderRadius: 20, padding: '0.3rem 0.75rem',
                    cursor: 'pointer', touchAction: 'manipulation', minHeight: 30,
                    flexShrink: 0,
                    transition: 'background 0.15s, border-color 0.15s, color 0.15s',
                  }}
                >{active && key === 'MEMORISED' ? '✓ ' : ''}{label}</button>
              );
            })}
          </div>
        )}

        {/* Tabs */}
        <div ref={tabBarRef} style={{ display: 'flex', borderBottom: '1px solid var(--border)', position: 'relative', zIndex: 1 }}>
          {['Read', 'Study', 'Reference'].map(t => (
            <button key={t} className={`rt-tab${activeTab === t ? ' active' : ''}`} onClick={() => setActiveTab(t)}>{t}</button>
          ))}
        </div>

        {/* ── READ TAB ── */}
        {activeTab === 'Read' && (
          <>
            {/* Context panel */}
            <div style={{ padding: '0.75rem 0 0', position: 'relative', zIndex: 1 }}>
              <div className={`context-panel${ctxOpen ? ' open' : ''}`}>
                <div ref={ctxRef} className="context-toggle" onClick={openCtx}>
                  <div className="context-toggle-icon">📖</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="context-toggle-label">Poem Context &amp; Interpretation</div>
                    <div className="context-toggle-sub">Poet · Contemporaries · Theme · Interpretations · Reception</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    <div className="lang-toggle">
                      <button className={`lang-btn${lang === 'ar' ? ' active' : ''}`} onClick={() => setLang('ar')}>AR</button>
                      <button className={`lang-btn${lang === 'en' ? ' active' : ''}`} onClick={() => setLang('en')}>EN</button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.1rem' }}>
                      <div ref={fontSizeRef} className="lang-toggle">
                        <button className={`lang-btn${ctxSize === 'sm' ? ' active' : ''}`} onClick={e => cycleCtxSize(-1, e)}>A−</button>
                        <button className={`lang-btn${ctxSize === 'md' ? ' active' : ''}`} onClick={e => { e.stopPropagation(); setCtxSize('md'); localStorage.setItem('diwanCtxSize', 'md'); if (!ctxOpen) setCtxOpen(true); }}>A</button>
                        <button className={`lang-btn${ctxSize === 'lg' ? ' active' : ''}`} onClick={e => cycleCtxSize(1, e)}>A+</button>
                      </div>
                      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.42rem', letterSpacing: '0.05em', color: 'var(--text3)', opacity: 0.5, pointerEvents: 'none' }}>pinch to zoom</div>
                    </div>
                  </div>
                  <span className="context-chevron">›</span>
                </div>
                {ctxOpen && (
                  <div className="context-body" style={{ '--ctx-zoom': CTX_SIZES[ctxSize] }}>
                    <div className="ctx-tabs">
                      {CTX_TABS.map(t => (
                        <button key={t} className={`ctx-tab${ctxTab === t ? ' active' : ''}`} onClick={() => setCtxTab(t)}>{t}</button>
                      ))}
                    </div>
                    {ctxLoading ? (
                      <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text3)', fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                        Generating analysis…
                      </div>
                    ) : ctxError ? (
                      <div style={{ textAlign: 'center', padding: '1.5rem 1rem' }}>
                        <div style={{ fontSize: '1.4rem', marginBottom: '0.5rem' }}>📖</div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.3rem' }}>
                          Analysis temporarily unavailable
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--text3)', lineHeight: 1.6 }}>
                          We're having trouble reaching our analysis service right now. Please try again in a moment.
                        </div>
                      </div>
                    ) : (
                      <div style={{ animation: 'fadeUp 0.2s ease' }}>
                        {/* ── Poet tab — shows image only when imageUrl exists; no fallback placeholder ── */}
                        {ctxTab === 'Poet' && (() => {
                          const poet = poem?.poet;
                          const BIO_PREVIEW_LEN = 220;
                          const isEnglish = lang && lang !== 'ar';
                          const bioText = (isEnglish && poet?.bioEn) ? poet.bioEn : (poet?.bio || '');
                          const bioIsAr  = !(isEnglish && poet?.bioEn);
                          const isLong = bioText.length > BIO_PREVIEW_LEN;
                          const displayedBio = isLong && !bioExpanded
                            ? bioText.slice(0, BIO_PREVIEW_LEN).trimEnd() + '…'
                            : bioText;
                          const poetLabel = (isEnglish && poet?.nameEn) ? poet.nameEn : poem.poetAr;
                          const imageUrl = poet?.imageUrl ?? null;
                          return (
                            <div style={{ padding: '0.25rem 0' }}>
                              {imageUrl && (
                                <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
                                  <img
                                    src={imageUrl}
                                    alt={poem.poetAr}
                                    style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--gold)', background: 'var(--bg2)' }}
                                    onError={e => { e.target.style.display = 'none'; }}
                                  />
                                </div>
                              )}
                              <div style={{ fontFamily: bioIsAr ? "'Noto Naskh Arabic', serif" : "'Josefin Sans', sans-serif", fontSize: '1rem', fontWeight: 600, textAlign: 'center', direction: bioIsAr ? 'rtl' : 'ltr', color: 'var(--gold)', marginBottom: '0.6rem' }}>{poetLabel}</div>
                              {bioText ? (
                                <div className="ctx-section-card" style={{ marginTop: '0.5rem' }}>
                                  <div className={bioIsAr ? 'ctx-section-label-ar' : 'ctx-section-label'}>{bioIsAr ? 'السيرة الذاتية' : 'Biography'}</div>
                                  <div style={{ fontFamily: bioIsAr ? "'Noto Naskh Arabic', serif" : "inherit", fontSize: '0.9rem', lineHeight: 1.9, direction: bioIsAr ? 'rtl' : 'ltr', textAlign: bioIsAr ? 'right' : 'left', color: 'var(--text)' }}>{displayedBio}</div>
                                  {isLong && (
                                    <div style={{ textAlign: 'center', marginTop: '0.6rem' }}>
                                      <button
                                        onClick={() => setBioExpanded(v => !v)}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', color: 'var(--gold)', opacity: 0.8, fontFamily: "'Josefin Sans', sans-serif", letterSpacing: '0.05em', padding: '0.2rem 0.5rem' }}
                                      >
                                        {bioExpanded ? '▲ Show less' : '▼ Read full biography'}
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div style={{ textAlign: 'center', padding: '0.75rem 0', color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.82rem' }}>
                                  Biography coming soon.
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* ── Contemporaries ── */}
                        {ctxTab === 'Contemporaries' && (
                          contempLoading ? (
                            <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text3)', fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.6rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                              Generating profile…
                            </div>
                          ) : contempError ? (
                            <div style={{ textAlign: 'center', padding: '1.5rem 1rem' }}>
                              <div style={{ fontSize: '1.4rem', marginBottom: '0.5rem' }}>📖</div>
                              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.3rem' }}>
                                Profile temporarily unavailable
                              </div>
                              <div style={{ fontSize: '0.78rem', color: 'var(--text3)', lineHeight: 1.6 }}>
                                We're compiling this poet's literary profile right now. Please try again in a moment.
                              </div>
                            </div>
                          ) : !contemp ? (
                            <div style={{ textAlign: 'center', padding: '1rem 0', color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem' }}>
                              {poem?.poet?.id ? 'Loading contemporaries…' : 'Contemporary information not available for this poet.'}
                            </div>
                          ) : (
                            <div style={{ animation: 'fadeUp 0.2s ease' }}>
                              {/* Adversaries */}
                              <div className="ctx-section-card">
                                <div className={lang === 'ar' ? 'ctx-section-label-ar' : 'ctx-section-label'}>{lang === 'ar' ? 'الخصوم والمنافسون' : 'Rivals & Adversaries'}</div>
                                <div className={lang === 'ar' ? 'ctx-ar-text' : 'ctx-body'} style={{ fontSize: '0.87rem' }}>{lang === 'ar' ? contemp.adversariesAr : contemp.adversariesEn}</div>
                              </div>
                              {/* Contemporaries list */}
                              {(contemp.contemporaries || []).length > 0 && (
                                <>
                                  <div className={lang === 'ar' ? 'ctx-section-label-ar' : 'ctx-section-label'} style={{ marginBottom: '0.5rem', opacity: 1 }}>
                                    {lang === 'ar' ? 'الشعراء المعاصرون' : 'Contemporary Poets'}
                                  </div>
                                  {contemp.contemporaries.map((c, i) => (
                                    <div className="ctx-interpretation" key={i} style={{ marginBottom: '0.5rem' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.25rem', flexDirection: lang === 'ar' ? 'row-reverse' : 'row' }}>
                                        <div className={lang === 'ar' ? 'ctx-ar-interp-label' : 'ctx-interp-label'} style={{ fontSize: lang === 'ar' ? '0.85rem' : '0.62rem', opacity: 1 }}>
                                          {lang === 'ar' ? c.nameAr : c.nameEn}
                                        </div>
                                        <div style={{ fontFamily: lang === 'ar' ? "'Noto Naskh Arabic', serif" : "'Josefin Sans', sans-serif", fontSize: '0.58rem', color: 'var(--gold)', opacity: 0.75, letterSpacing: lang === 'ar' ? 0 : '0.08em', direction: lang === 'ar' ? 'rtl' : 'ltr' }}>
                                          {lang === 'ar' ? c.locationAr : c.locationEn}
                                        </div>
                                      </div>
                                      <div className={lang === 'ar' ? 'ctx-ar-text' : 'ctx-interp-body'} style={{ fontSize: '0.82rem' }}>
                                        {lang === 'ar' ? c.noteAr : c.noteEn}
                                      </div>
                                    </div>
                                  ))}
                                </>
                              )}
                              {/* World context */}
                              <div className="ctx-section-card" style={{ marginTop: '0.25rem' }}>
                                <div className={lang === 'ar' ? 'ctx-section-label-ar' : 'ctx-section-label'}>{lang === 'ar' ? 'المشهد الأدبي في العالم الإسلامي' : 'Islamic World Literary Landscape'}</div>
                                <div className={lang === 'ar' ? 'ctx-ar-text' : 'ctx-body'} style={{ fontSize: '0.87rem' }}>{lang === 'ar' ? contemp.worldContextAr : contemp.worldContextEn}</div>
                              </div>
                            </div>
                          )
                        )}

                        {/* ── Theme + historical context + Bayan sections 1 & 2 ── */}
                        {ctxTab === 'Theme' && (ctxData ? (
                          <div>
                            <div className="ctx-section-card">
                              <div className={lang === 'ar' ? 'ctx-section-label-ar' : 'ctx-section-label'}>{lang === 'ar' ? 'الفكرة العامة' : 'Theme'}</div>
                              <div className={lang === 'ar' ? 'ctx-ar-text' : 'ctx-body'}>{lang === 'ar' ? stripArabicPrefix(ctxData.themeAr, 'الفكرة العامة') : ctxData.themeEn}</div>
                            </div>
                            {lang === 'ar' && ctxData.historicalContextAr && (
                              <div className="ctx-section-card">
                                <div className="ctx-section-label-ar">السياق التاريخي</div>
                                <div className="ctx-ar-text" style={{ fontSize: '0.88rem' }}>{ctxData.historicalContextAr}</div>
                              </div>
                            )}
                            {(!lang || lang === 'en') && ctxData.historicalContextEn && (
                              <div className="ctx-section-card">
                                <div className="ctx-section-label">Historical Context</div>
                                <div className="ctx-body" style={{ fontSize: '0.88rem' }}>{ctxData.historicalContextEn}</div>
                              </div>
                            )}
                            {lang === 'ar' && poem?.bayanExplanation && (() => {
                              const sections = poem.bayanExplanation.split(/###\s*\d+\)?\s*/);
                              const s1 = (sections[1] || '').replace(/^الفكرة العامة[:\s]*/i, '').trim();
                              const s2 = (sections[2] || '').replace(/^الموضوعات[:\s]*/i, '').trim();
                              if (!s1 && !s2) return null;
                              return (
                                <div className="ctx-section-card">
                                  <div className="ctx-section-label-ar" style={{ opacity: 1 }}>تحليل بيان</div>
                                  {s1 && <div className="ctx-ar-text" style={{ fontSize: '0.87rem', marginBottom: s2 ? '0.6rem' : 0 }}>{s1}</div>}
                                  {s2 && <div className="ctx-ar-text" style={{ fontSize: '0.85rem', whiteSpace: 'pre-line' }}>{stripMd(s2)}</div>}
                                </div>
                              );
                            })()}
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '1rem 0', color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem' }}>Open to generate context for this poem.</div>
                        ))}

                        {/* ── Interpretations + Bayan section 4 (خلاصة نقاط) ── */}
                        {ctxTab === 'Interpretations' && (ctxData ? (
                          <div>
                            {lang === 'ar' && poem?.bayanExplanation && (() => {
                              const sections = poem.bayanExplanation.split(/###\s*\d+\)?\s*/);
                              const s4 = (sections[4] || '').replace(/^خلاصة نقاط[:\s]*/i, '').trim();
                              if (!s4) return null;
                              return (
                                <div className="ctx-section-card" style={{ marginBottom: '0.85rem' }}>
                                  <div className="ctx-section-label-ar" style={{ opacity: 1 }}>خلاصة بيان</div>
                                  <div className="ctx-ar-text" style={{ fontSize: '0.86rem', whiteSpace: 'pre-line' }}>{s4}</div>
                                </div>
                              );
                            })()}
                            {(ctxData.interpretations || []).length > 0 && (
                              <div className={lang === 'ar' ? 'ctx-section-label-ar' : 'ctx-section-label'} style={{ marginBottom: '0.5rem', opacity: 1 }}>
                                {lang === 'ar' ? 'التفسيرات الأدبية' : 'Literary Interpretations'}
                              </div>
                            )}
                            {(ctxData.interpretations || []).map((interp, i) => (
                              <div className="ctx-interpretation" key={i}>
                                <div className={lang === 'ar' ? 'ctx-ar-interp-label' : 'ctx-interp-label'}>{lang === 'ar' ? stripMd(interp.nameAr) : interp.nameEn}</div>
                                <div className={lang === 'ar' ? 'ctx-ar-text' : 'ctx-interp-body'} style={{ fontSize: '0.83rem' }}>{lang === 'ar' ? stripMd(interp.descriptionAr) : interp.descriptionEn}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '1rem 0', color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem' }}>Open to generate context for this poem.</div>
                        ))}

                        {/* ── Reception ── */}
                        {ctxTab === 'Reception' && (ctxData ? (
                          <div className="ctx-section-card">
                            <div className={lang === 'ar' ? 'ctx-section-label-ar' : 'ctx-section-label'}>{lang === 'ar' ? 'تاريخ الاستقبال النقدي' : 'Critical Reception'}</div>
                            <div className={lang === 'ar' ? 'ctx-ar-text' : 'ctx-body'} style={{ whiteSpace: 'pre-line' }}>{lang === 'ar' ? stripArabicPrefix(ctxData.receptionHistoryAr, 'خلاصة نقاط') : ctxData.receptionHistoryEn}</div>
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '1rem 0', color: 'var(--text3)', fontStyle: 'italic', fontSize: '0.85rem' }}>Open to generate context for this poem.</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Verse reader */}
            <div style={{ padding: '1.5rem 1.5rem 1rem', position: 'relative', zIndex: 1 }}>
              <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
                <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--text3)' }}>
                  Bayt {verseIdx + 1} of {displayVerses.length}
                </div>
                <div style={{ marginTop: '0.25rem', display: 'flex', justifyContent: 'center' }}>
                  <DepthLegend depths={depths} total={displayVerses.length} />
                </div>
              </div>

              {verse && (
                <div style={{ position: 'relative', marginBottom: '1rem' }}>
                  <div ref={verseAreaRef} style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.5rem', lineHeight: 2.3, direction: 'rtl', textAlign: 'center', color: 'var(--text)', padding: '1.75rem 1.5rem', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6 }}>
                    {verseWords.map((w, i) => (
                      <span key={i}>
                        <span
                          className={`poem-word${tappedWord?._idx === i ? ' active' : ''}`}
                          onClick={() => setTappedWord(tappedWord?._idx === i ? null : { ar: w.ar, _idx: i })}
                        >{w.ar}</span>
                        {w.br ? <><br /><span className="caesura-marker" /></> : (i < verseWords.length - 1 ? ' ' : '')}
                      </span>
                    ))}
                  </div>
                  {/* Share button + sheet */}
                  <div style={{ position: 'absolute', top: '0.5rem', right: '0.5rem' }}>
                    <button
                      onClick={() => setShareOpen(s => !s)}
                      aria-label="Share"
                      style={{ background: shareOpen ? 'var(--bg2)' : 'var(--bg3)', border: `1px solid ${shareOpen ? 'var(--border2)' : 'var(--border)'}`, borderRadius: 4, padding: '0.25rem 0.4rem', cursor: 'pointer', color: verseCopied ? 'var(--gold)' : 'var(--text3)', transition: 'color 0.15s', lineHeight: 1 }}
                    >
                      {verseCopied ? (
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="2,8 6,12 14,4" />
                        </svg>
                      ) : navigator.share ? (
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M8 1v9"/><path d="M5 4l3-3 3 3"/><path d="M3 9v6h10V9"/>
                        </svg>
                      ) : (
                        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="5" y="5" width="9" height="9" rx="1"/><path d="M3 11H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v1"/>
                        </svg>
                      )}
                    </button>
                    {shareOpen && (() => {
                      const fmtV = (v) => (v?.textAr || '').replace(' / ', '\n');
                      const attr = `\n\n— ${poem.poetAr || ''}\n\nvia Al-Diwan · aldiwan.uk`;
                      const doShare = (bodyText) => {
                        setShareOpen(false);
                        const confirm = () => { setVerseCopied(true); setTimeout(() => setVerseCopied(false), 2000); };
                        const full = bodyText + attr;
                        if (navigator.share) {
                          navigator.share({ title: poem.titleAr || 'Al-Diwan', text: full, url: window.location.href }).then(confirm).catch(() => {});
                        } else {
                          navigator.clipboard.writeText(full).then(confirm);
                        }
                      };
                      const total = displayVerses.length;
                      const options = [
                        { label: 'This bayt', text: fmtV(verse) },
                        verseIdx > 0            && { label: `Bayt ${verseIdx} & ${verseIdx + 1}`, text: fmtV(displayVerses[verseIdx - 1]) + '\n\n' + fmtV(verse) },
                        verseIdx < total - 1    && { label: `Bayt ${verseIdx + 1} & ${verseIdx + 2}`, text: fmtV(verse) + '\n\n' + fmtV(displayVerses[verseIdx + 1]) },
                        { label: 'Full poem', text: (poem.titleAr ? poem.titleAr + '\n\n' : '') + displayVerses.map((v, i) => `${i + 1}. ${fmtV(v)}`).join('\n\n') },
                      ].filter(Boolean);
                      return (
                        <>
                          <div onClick={() => setShareOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 9 }} />
                          <div style={{ position: 'absolute', top: 'calc(100% + 0.35rem)', right: 0, background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 6, padding: '0.3rem', zIndex: 10, minWidth: 170, boxShadow: '0 4px 20px rgba(0,0,0,0.35)', animation: 'fadeUp 0.12s ease' }}>
                            {options.map((opt) => (
                              <button
                                key={opt.label}
                                onClick={() => doShare(opt.text)}
                                style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem 0.65rem', borderRadius: 4, fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.55rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text2)', whiteSpace: 'nowrap', transition: 'background 0.1s' }}
                                onMouseEnter={e => e.currentTarget.style.background = 'var(--bg3)'}
                                onMouseLeave={e => e.currentTarget.style.background = 'none'}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Subtle loading hint — shown while analysis is still fetching */}
              {verseLoading && !tappedWord && (
                <div style={{ textAlign: 'center', marginBottom: '0.65rem', marginTop: '-0.25rem' }}>
                  <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.5rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--gold)', opacity: 0.55, animation: 'pulse 1.8s ease-in-out infinite' }}>
                    ◆ loading definitions…
                  </span>
                </div>
              )}

              {/* Word tooltip */}
              {tappedWord && (() => {
                const vocabEntry = findVocabEntry(analysis?.vocabulary, tappedWord.ar);
                return (
                  <div style={{ background: 'var(--bg2)', border: '1px solid var(--em)', borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '1rem', animation: 'fadeUp 0.2s ease' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '0.25rem', flexDirection: 'row-reverse', justifyContent: 'flex-end' }}>
                      <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.4rem', direction: 'rtl', color: 'var(--gold)' }}>{tappedWord.ar}</div>
                      {vocabEntry && <div style={{ fontSize: '0.78rem', color: 'var(--em)', fontStyle: 'italic' }}>{vocabEntry.romanisation}</div>}
                    </div>
                    {vocabEntry?.rootLetters && (
                      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: '0.45rem' }}>
                        Root: <span style={{ color: 'var(--gold)' }}>{vocabEntry.rootLetters}</span>
                      </div>
                    )}
                    {vocabEntry?.definitionAr && (
                      <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.92rem', direction: 'rtl', textAlign: 'right', color: 'var(--text2)', lineHeight: 1.7, padding: '0.5rem 0.65rem', background: 'rgba(229,150,83,0.06)', border: '1px solid rgba(229,150,83,0.13)', borderRadius: 3, marginBottom: '0.35rem' }}>{vocabEntry.definitionAr}</div>
                    )}
                    {vocabEntry?.meaningEn && (
                      <>
                        <div style={{ height: 1, background: 'var(--border2)', margin: '0.4rem 0' }} />
                        <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.55rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: '0.2rem' }}>English</div>
                        <div style={{ fontSize: '0.88rem', color: 'var(--text2)', lineHeight: 1.5 }}>{vocabEntry.meaningEn}</div>
                      </>
                    )}
                    {vocabEntry?.grammarNote && (
                      <div style={{ marginTop: '0.5rem', padding: '0.4rem 0.65rem', background: 'rgba(140,207,225,0.06)', border: '1px solid rgba(140,207,225,0.18)', borderRadius: 3, fontSize: '0.8rem', color: 'var(--text2)', fontStyle: 'italic', lineHeight: 1.5 }}>{vocabEntry.grammarNote}</div>
                    )}
                    {!vocabEntry && (() => {
                      if (verseLoading) {
                        return <div style={{ fontSize: '0.78rem', color: 'var(--text3)', fontStyle: 'italic', marginTop: '0.25rem' }}>Unrolling the scroll…</div>;
                      }
                      const gt = (Array.isArray(analysis?.grammarTokens) ? analysis.grammarTokens : []).find(t =>
                        t?.ar && normalizeArabicWord(t.ar) === normalizeArabicWord(tappedWord.ar)
                      );
                      const c = gt ? GRAMMAR_COLORS[gt.type] : null;
                      // Fallback meaning from rootFamilies — covers content words the AI
                      // didn't include in vocabulary on older cached analyses.
                      const normTapped = normalizeArabicWord(tappedWord.ar);
                      const rfWord = (analysis?.rootFamilies || []).flatMap(rf =>
                        (rf.words || []).map(w => ({ root: rf.root, ...w }))
                      ).find(w => normalizeArabicWord(w.ar) === normTapped
                        || normTapped.startsWith(normalizeArabicWord(w.ar)) && normalizeArabicWord(w.ar).length >= 3
                      );
                      return (
                        <div style={{ marginTop: '0.25rem' }}>
                          {c && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.45rem', flexWrap: 'wrap' }}>
                              <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: c.text, background: c.bg, border: `1px solid ${c.border}`, borderRadius: 3, padding: '0.2rem 0.4rem' }}>
                                {c.label}
                              </span>
                              {gt.roleAr && (
                                <span style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.85rem', color: 'var(--text2)', direction: 'rtl' }}>
                                  {gt.roleAr}
                                </span>
                              )}
                            </div>
                          )}
                          {gt?.type === 'particle' ? (
                            <div style={{ fontSize: '0.78rem', color: 'var(--text3)', fontStyle: 'italic' }}>
                              Function word — the grammar glue of classical Arabic
                            </div>
                          ) : rfWord ? (
                            <>
                              {rfWord.root && (
                                <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: '0.2rem' }}>
                                  Root: <span style={{ color: 'var(--gold)' }}>{rfWord.root}</span>
                                </div>
                              )}
                              <div style={{ fontSize: '0.88rem', color: 'var(--text2)', lineHeight: 1.5 }}>{rfWord.meaningEn}</div>
                            </>
                          ) : noBalance ? (
                            <>
                              <div style={{ fontSize: '0.78rem', color: 'var(--text3)', lineHeight: 1.55 }}>
                                This poem hasn't been analysed yet. Top up your wallet to unlock word definitions, grammar & literary devices — once cached, it's free for all future readers.
                              </div>
                              <button
                                className="pill-btn"
                                style={{ fontSize: '0.58rem', marginTop: '0.5rem' }}
                                onClick={e => { e.stopPropagation(); setTappedWord(null); setShowAnalysisModal(true); }}
                              >
                                Top up & analyse →
                              </button>
                            </>
                          ) : (
                            <div style={{ fontSize: '0.78rem', color: 'var(--text3)', fontStyle: 'italic' }}>
                              {analysis
                                ? 'Word not found in the cached vocabulary for this verse.'
                                : 'Switch to Study tab to unlock word definitions — vocabulary loads when this verse is analysed.'}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.7rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      {vocabEntry ? (
                        optimisticSaved.has(vocabEntry.wordAr) || savedWords.some(s => s.wordAr === vocabEntry.wordAr) ? (
                          <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#34d399' }}>✓ Saved</span>
                        ) : (
                          <button className="pill-btn" style={{ fontSize: '0.58rem' }} onClick={() => saveWord(vocabEntry)}>＋ Save Word</button>
                        )
                      ) : !analysis && !verseLoading && (
                        <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.52rem', letterSpacing: '0.08em', color: 'var(--text3)', opacity: 0.7 }}>
                          Save word after analysis
                        </span>
                      )}
                      <button className="pill-btn" style={{ fontSize: '0.58rem' }} onClick={() => setTappedWord(null)}>✕ Close</button>
                    </div>
                  </div>
                );
              })()}

              {/* Translation reveal — powered by Claude */}
              <div
                onClick={() => setTransRevealed(r => !r)}
                style={{ background: 'rgba(140,207,225,0.07)', border: '1px solid rgba(140,207,225,0.22)', borderRadius: 6, padding: '0.85rem 1.1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', cursor: 'pointer', minHeight: 48 }}
              >
                {transRevealed ? (
                  !quickTrans[verseIdx] || quickTrans[verseIdx]?._retrying ? (
                    <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)' }}>
                      {quickTrans[verseIdx]?._retrying
                        ? `Retrying in ${transCountdown ?? '…'}s`
                        : 'Translating…'}
                    </span>
                  ) : quickTrans[verseIdx]?._error ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '0.75rem' }}>
                      <span style={{ fontStyle: 'italic', color: 'var(--text3)', fontSize: '0.82rem', lineHeight: 1.5 }}>
                        {quickTrans[verseIdx]?._is429
                          ? 'Service is busy right now — please try again in a moment.'
                          : 'Translation temporarily unavailable.'}
                      </span>
                      <button
                        onClick={e => { e.stopPropagation(); setQuickTrans(prev => { const n = { ...prev }; delete n[verseIdx]; return n; }); }}
                        style={{ flexShrink: 0, background: 'none', border: '1px solid var(--border)', borderRadius: 3, padding: '0.3rem 0.65rem', fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.5rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--em)', cursor: 'pointer' }}
                      >↺ Retry</button>
                    </div>
                  ) : (
                    <span style={{ fontStyle: 'italic', color: 'var(--text2)', fontSize: '0.88rem', lineHeight: 1.6 }}>
                      {quickTrans[verseIdx]?.translationEn}
                    </span>
                  )
                ) : (
                  <>
                    <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.6rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text3)' }}>Translation Hidden</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      {alwaysShowTrans ? (
                        <button
                          onClick={e => { e.stopPropagation(); setAlwaysShowTrans(false); lsSet('diwanTransPref', 'hide'); }}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.48rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)', opacity: 0.7 }}
                        >auto · off</button>
                      ) : (
                        <button
                          onClick={e => { e.stopPropagation(); setAlwaysShowTrans(true); lsSet('diwanTransPref', 'show'); setTransRevealed(true); }}
                          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.48rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)', opacity: 0.7 }}
                        >Always show</button>
                      )}
                      <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--em)', border: '1px solid var(--em)', padding: '0.45rem 0.85rem', borderRadius: 3, display: 'inline-flex', alignItems: 'center', minHeight: 32 }}>Reveal →</span>
                    </div>
                  </>
                )}
              </div>

              {/* Study discovery hint — shown on every bayt when translation is revealed; resets on bayt change */}
              {transRevealed && !studyHintSeen && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.65rem', padding: '0.55rem 0.85rem', background: 'rgba(229,150,83,0.06)', border: '1px solid rgba(229,150,83,0.18)', borderRadius: 4, animation: 'fadeUp 0.25s ease' }}>
                  <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.52rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)' }}>
                    Go deeper — tap Study for full analysis
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                    <button
                      onClick={() => { setActiveTab('Study'); setStudyHintSeen(true); }}
                      style={{ background: 'none', border: '1px solid rgba(229,150,83,0.5)', borderRadius: 3, padding: '0.25rem 0.6rem', fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.52rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--gold)', cursor: 'pointer' }}
                    >Study →</button>
                    <button
                      onClick={() => setStudyHintSeen(true)}
                      aria-label="Dismiss"
                      style={{ background: 'none', border: 'none', padding: '0.2rem 0.3rem', cursor: 'pointer', color: 'var(--text3)', fontSize: '0.65rem', lineHeight: 1 }}
                    >✕</button>
                  </div>
                </div>
              )}

              {/* Navigation + verse depth strip */}
              <div ref={navRef} style={{ marginTop: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <button className="bayt-arrow" onClick={() => changeVerse(-1)} disabled={verseIdx === 0}>←</button>
                  <VerseDepthStrip
                    total={displayVerses.length}
                    depths={depths}
                    current={verseIdx}
                    onNavigate={i => { setVerseIdx(i); setTappedWord(null); setAnalysisFailed(false); setAnalysisErrCode(null); }}
                  />
                  <button className="bayt-arrow" onClick={() => changeVerse(1)} disabled={verseIdx === displayVerses.length - 1}>→</button>
                </div>
                {depths[verseIdx] !== 'ai' && (
                  <div style={{ textAlign: 'center', marginTop: '0.6rem' }}>
                    {(!depths[verseIdx] || depths[verseIdx] === 'read') ? (
                      <button
                        onClick={() => markManual(verseIdx)}
                        style={{
                          background: 'none', border: '1px solid var(--border)', borderRadius: 20,
                          padding: '0.35rem 1rem', cursor: 'pointer',
                          fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem',
                          letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text3)',
                          touchAction: 'manipulation', minHeight: 36,
                        }}
                      >Mark as studied</button>
                    ) : (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem' }}>
                        <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--gold)' }}>✓ Studied</span>
                        <button
                          onClick={() => unmarkStudy(verseIdx)}
                          style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 20, padding: '0.2rem 0.65rem', cursor: 'pointer', fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.52rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)', touchAction: 'manipulation', minHeight: 30 }}
                        >Undo</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Show full poem */}
            <div style={{ padding: '0 1.5rem 2rem', position: 'relative', zIndex: 1 }}>
              <button
                onClick={() => setShowFull(f => !f)}
                style={{ width: '100%', padding: '0.85rem', background: 'transparent', border: '1px solid var(--border)', borderRadius: 4, fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.62rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text3)', cursor: 'pointer' }}
              >
                {showFull ? 'Hide Full Poem' : `Show Full Poem (${displayVerses.length} Bayts)`}
              </button>
              {showFull && (
                <div style={{ marginTop: '1rem', fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.05rem', direction: 'rtl', lineHeight: 2.1, color: 'var(--text2)', padding: '1rem', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6 }}>
                  {displayVerses.map((v, i) => (
                    <div key={i} onClick={() => { setVerseIdx(i); setShowFull(false); }} style={{ cursor: 'pointer', padding: '0.25rem 0', borderBottom: i < displayVerses.length - 1 ? '1px solid var(--border2)' : 'none', color: i === verseIdx ? 'var(--gold)' : 'var(--text2)', transition: 'color 0.15s' }}>
                      <span style={{ fontSize: '0.65rem', color: 'var(--text3)', fontFamily: "'Josefin Sans', sans-serif", direction: 'ltr', display: 'inline-block', marginLeft: '0.5rem' }}>{i + 1}</span>
                      {v.textAr.includes(' / ') ? (
                        <>
                          {v.textAr.split(' / ')[0]}
                          <br />
                          <span className="caesura-marker" />
                          {v.textAr.split(' / ').slice(1).join(' / ')}
                        </>
                      ) : v.textAr}
                    </div>
                  ))}
                </div>
              )}
            </div>

          {poem && verse && (
            <div ref={askAiRef}>
              <AskAiSection
                poemId={poemId}
                poem={poem}
                verse={verse}
                verseIdx={verseIdx}
                displayVerses={displayVerses}
                onAIOpened={() => markAI(verseIdx)}
                onBackfillDepth={backfillFromSession}
                onPasteBackSaved={() => {
                  // Auto-set Studying when the user first engages with AI paste-back.
                  // Don't fire if already at STUDYING (would toggle it off) or higher.
                  if (!memoryStatus || memoryStatus === 'FAVOURITE') handleMemoryStatus('STUDYING');
                }}
                onNavigateToVerse={(verseId) => {
                  let idx = displayVerses.findIndex(v => v.id === verseId);
                  if (idx < 0) {
                    const m = verseId?.match(/_v(\d+)$/);
                    if (m) idx = parseInt(m[1], 10);
                  }
                  if (idx >= 0 && idx < displayVerses.length) setVerseIdx(idx);
                }}
              />
            </div>
          )}
          </>
        )}

        {/* ── STUDY TAB ── */}
        {activeTab === 'Study' && (
          <div style={{ padding: '1.5rem', position: 'relative', zIndex: 1 }}>
            <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--text3)', textAlign: 'center', marginBottom: '1.25rem' }}>
              Bayt {verseIdx + 1} of {displayVerses.length}
            </div>

            {verse && (
              <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.3rem', lineHeight: 2.2, direction: 'rtl', textAlign: 'center', color: 'var(--text)', marginBottom: '1.25rem', padding: '1.25rem 1rem', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6 }}>
                {verseWords.map((w, i) => (
                  <span key={i}>{w.ar}{w.br ? <><br /><span className="caesura-marker" /></> : (i < verseWords.length - 1 ? ' ' : '')}</span>
                ))}
              </div>
            )}

            {!verseLoading && noBalance && !analysis && (
              <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 4, padding: '1.25rem', marginBottom: '0.5rem', textAlign: 'center' }}>
                <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: '0.6rem' }}>
                  Not yet analysed
                </div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text2)', lineHeight: 1.65, marginBottom: '0.35rem' }}>
                  This verse hasn't been analysed yet. Top up your wallet to unlock the tools below — once cached, they're permanently free for every future reader.
                </div>
                {!walletLoading && walletBalance !== null && (
                  <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.52rem', letterSpacing: '0.08em', color: 'var(--text3)', marginBottom: '0.6rem' }}>
                    Your balance: <span style={{ color: walletBalance > 0 ? 'var(--gold)' : 'var(--text3)' }}>£{((walletBalance ?? 0) / 100).toFixed(2)}</span>
                    {' '}· Analysis costs ~1–4p
                  </div>
                )}
                {/* Cost table */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: '0.6rem', marginBottom: '0.85rem', flexWrap: 'wrap' }}>
                  {[['Gemini', '~1p'], ['Sonnet', '~3p'], ['GPT-4o', '~4p']].map(([label, cost]) => (
                    <div key={label} style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.55rem', letterSpacing: '0.06em', color: 'var(--text3)', background: 'var(--bg2)', border: '1px solid var(--border2)', borderRadius: 3, padding: '0.3rem 0.6rem' }}>
                      {label} · {cost}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => setShowAnalysisModal(true)}
                  style={{ background: 'var(--em-glow)', border: '1px solid var(--em-line)', borderRadius: 3, padding: '0.5rem 1.25rem', fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--em)', cursor: 'pointer' }}
                >
                  Top up & analyse →
                </button>
              </div>
            )}

            {(verseLoading || (!analysis && !analysisFailed)) && (
              <div>
                {/* Progress bar — only during an active fetch, not in the noBalance locked state */}
                {verseLoading && (
                  <div style={{ marginBottom: '1.1rem' }}>
                    <div style={{ height: 2, background: 'var(--border2)', borderRadius: 1, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: '4%', background: 'var(--gold)', borderRadius: 1, animation: 'studybar 25s cubic-bezier(0.15,0,0.4,1) forwards' }} />
                    </div>
                    {slowLoad && (
                      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.52rem', letterSpacing: '0.08em', color: 'var(--text3)', textAlign: 'center', marginTop: '0.5rem' }}>
                        Generating analysis for the first time — this may take up to 20 seconds…
                      </div>
                    )}
                  </div>
                )}
                {/* Tool rows — ghost (loading) or locked (noBalance, clickable to open analysis modal) */}
                {[
                  ['🪄', 'Literary Devices', 'Rhetoric & imagery'],
                  ['📖', 'Vocabulary', '— words'],
                  ['🌱', 'Root Explorer', 'Word families'],
                  ['🏷', 'Grammar Layer', 'Colour-coded إعراب'],
                  ['◌',  'Harakat Trainer', 'Vocalise the verse'],
                  ['✏️', 'Cloze Exercise', 'Fill the gaps'],
                ].map(([icon, name, meta]) => (
                  <div
                    key={name}
                    onClick={noBalance ? () => setShowAnalysisModal(true) : undefined}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      padding: '0.8rem 1rem', background: 'var(--bg2)',
                      border: `1px solid ${noBalance ? 'var(--gold-line)' : 'var(--border2)'}`,
                      borderRadius: 4, marginBottom: '0.4rem',
                      opacity: noBalance ? 0.6 : 0.45,
                      cursor: noBalance ? 'pointer' : 'default',
                    }}
                  >
                    <span style={{ fontSize: '1rem', flexShrink: 0 }}>{noBalance ? '🔒' : icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.7rem', letterSpacing: '0.08em', color: 'var(--text2)' }}>{name}</div>
                      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.52rem', letterSpacing: '0.06em', color: 'var(--text3)', marginTop: '0.1rem' }}>
                        {noBalance ? 'Top up to unlock' : meta}
                      </div>
                    </div>
                    <span style={{ color: noBalance ? 'var(--gold)' : 'var(--border)', fontSize: '0.8rem' }}>›</span>
                  </div>
                ))}
              </div>
            )}

            {!verseLoading && !analysis && analysisFailed && (
              <div role="alert" style={{
                background: 'var(--bg3)',
                border: '1px solid var(--border)',
                borderRadius: 4,
                padding: '1rem 1.25rem',
                marginBottom: '0.75rem',
                textAlign: 'center',
              }}>
                <div style={{
                  fontFamily: "'Josefin Sans', sans-serif",
                  fontSize: '0.58rem',
                  letterSpacing: '0.13em',
                  textTransform: 'uppercase',
                  color: 'var(--text3)',
                  marginBottom: '0.5rem',
                }}>
                  Couldn't load study tools
                </div>
                <div style={{
                  fontSize: '0.82rem',
                  color: 'var(--text3)',
                  lineHeight: 1.6,
                  marginBottom: '0.85rem',
                  fontStyle: 'italic',
                }}>
                  We ran into a problem loading this verse's analysis. This is usually a temporary connection issue — please try again.
                </div>
                <button
                  onClick={() => {
                    analysisRetryRef.current = 0;
                    setAnalysisFailed(false);
                    setAnalysisErrCode(null);
                    setAnalysisRetryCount(c => c + 1);
                  }}
                  style={{
                    background: 'transparent',
                    border: '1px solid var(--border)',
                    borderRadius: 3,
                    padding: '0.5rem 1.25rem',
                    fontFamily: "'Josefin Sans', sans-serif",
                    fontSize: '0.58rem',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: 'var(--em)',
                    cursor: 'pointer',
                  }}
                >
                  Try again →
                </button>
              </div>
            )}

            {analysis && (
              <div ref={studySectionRef}>
                {analysis.explanationAr && (
                  <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 4, padding: '0.85rem 1rem', marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.55rem', letterSpacing: '0.13em', textTransform: 'uppercase', color: 'var(--text3)' }}>
                        Plain Reading
                      </div>
                      {analysis.explanationAr && analysis.translationEn && (
                        <div className="lang-toggle" onClick={e => e.stopPropagation()}>
                          <button className={`lang-btn${plainReadingAr ? ' active' : ''}`} onClick={() => setPlainReadingAr(true)}>AR</button>
                          <button className={`lang-btn${!plainReadingAr ? ' active' : ''}`} onClick={() => setPlainReadingAr(false)}>EN</button>
                        </div>
                      )}
                    </div>
                    {plainReadingAr ? (
                      analysis.explanationAr ? (
                        <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.9rem', direction: 'rtl', textAlign: 'right', color: 'var(--text2)', lineHeight: 1.8 }}>
                          {analysis.explanationAr}
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.78rem', color: 'var(--text3)', fontFamily: "'Josefin Sans', sans-serif", letterSpacing: '0.06em' }}>
                          Arabic reading not available for this verse.
                        </div>
                      )
                    ) : (
                      <div style={{ fontSize: '0.88rem', color: 'var(--text2)', lineHeight: 1.65 }}>
                        {analysis.explanationEn || plainReadingEnCache[verseIdx]
                          || (plainReadingEnLoading ? 'Translating…' : analysis.translationEn)}
                      </div>
                    )}
                  </div>
                )}
                <StudyTool icon="🪄" name="Literary Devices" meta="Rhetoric & imagery"
                  isOpen={openTool === 'literary'} onToggle={() => setOpenTool(t => t === 'literary' ? null : 'literary')}>
                  <DbLiteraryDevices key={verseIdx} analysis={analysis} />
                  {poem?.bayanExplanation && (() => {
                    const sections = poem.bayanExplanation.split(/###\s*\d+\)?\s*/);
                    const s3 = (sections[3] || '').replace(/^الصور\/الأساليب[:\s]*/i, '').trim();
                    if (!s3) return null;
                    const bayanDevices = s3
                      .split('\n')
                      .map(line => {
                        const m = line.match(/^\s*-?\s*\*\*([^*]+)\*\*\s*[:：]\s*(.+)$/);
                        return m ? { nameAr: m[1].trim(), explanation: m[2].trim() } : null;
                      })
                      .filter(Boolean);
                    if (!bayanDevices.length) return null;
                    return (
                      <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {bayanDevices.map((d, i) => (
                          <div key={i} style={{ background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 4, padding: '0.9rem 1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                              <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.1rem', color: 'var(--gold)', direction: 'rtl' }}>{d.nameAr}</div>
                              <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--em)', background: 'rgba(140,207,225,0.1)', border: '1px solid rgba(140,207,225,0.22)', borderRadius: 3, padding: '0.2rem 0.45rem' }}>بيان</div>
                            </div>
                            <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.87rem', color: 'var(--text2)', lineHeight: 1.75, direction: 'rtl', textAlign: 'right' }}>{d.explanation}</div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </StudyTool>

                <StudyTool icon="📖" name="Vocabulary" meta={`${(analysis.vocabulary || []).length} words`}
                  isOpen={openTool === 'vocab'} onToggle={() => setOpenTool(t => t === 'vocab' ? null : 'vocab')}>
                  {(() => {
                    const savedSet = new Set([...savedWords.map(s => s.wordAr), ...optimisticSaved]);
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.25rem' }}>
                          <div className="lang-toggle">
                            <button className={`lang-btn${vocabLang === 'ar' ? ' active' : ''}`} onClick={() => setVocabLang('ar')}>AR</button>
                            <button className={`lang-btn${vocabLang === 'en' ? ' active' : ''}`} onClick={() => setVocabLang('en')}>EN</button>
                          </div>
                        </div>
                        {(Array.isArray(analysis.vocabulary) ? analysis.vocabulary : []).filter(Boolean).map((w, i) => {
                          const alreadySaved = savedSet.has(w.wordAr);
                          return (
                            <div key={i} style={{ background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 4, padding: '0.75rem 0.9rem' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.3rem' }}>
                                <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.15rem', color: 'var(--gold)', direction: 'rtl' }}>{w.wordAr}</div>
                                <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.06em', color: 'var(--text3)', textTransform: 'uppercase' }}>{w.rootLetters}</div>
                              </div>
                              <div style={{ fontSize: '0.58rem', color: 'var(--em)', letterSpacing: '0.06em', fontFamily: "'Josefin Sans', sans-serif", marginBottom: '0.2rem' }}>{w.romanisation}</div>
                              {vocabLang === 'ar' ? (
                                w.definitionAr && (
                                  <div style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.83rem', direction: 'rtl', textAlign: 'right', color: 'var(--text2)', lineHeight: 1.55, marginTop: '0.2rem' }}>{w.definitionAr}</div>
                                )
                              ) : (
                                <div style={{ fontSize: '0.83rem', color: 'var(--text2)', lineHeight: 1.55, marginTop: '0.2rem' }}>{w.meaningEn}</div>
                              )}
                              <div style={{ marginTop: '0.55rem' }}>
                                {alreadySaved ? (
                                  <span style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.55rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#34d399' }}>✓ Saved</span>
                                ) : (
                                  <button
                                    onClick={() => saveWord(w)}
                                    style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.55rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--em)', background: 'rgba(140,207,225,0.08)', border: '1px solid rgba(140,207,225,0.3)', borderRadius: 3, padding: '0.3rem 0.65rem', cursor: 'pointer' }}
                                  >＋ Save</button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </StudyTool>

                <StudyTool icon="🌱" name="Root Explorer" meta="Word families"
                  isOpen={openTool === 'root'} onToggle={() => setOpenTool(t => t === 'root' ? null : 'root')}>
                  <DbRootExplorer key={verseIdx} analysis={analysis} />
                </StudyTool>

                <StudyTool icon="🏷" name="Grammar Layer" meta="Colour-coded إعراب"
                  isOpen={openTool === 'grammar'} onToggle={() => setOpenTool(t => t === 'grammar' ? null : 'grammar')}>
                  <DbGrammarLayer key={verseIdx} verseWords={verseWords} analysis={analysis} />
                </StudyTool>

                <StudyTool icon="◌" name="Harakat Trainer" meta="Vocalise the verse"
                  isOpen={openTool === 'harakat'} onToggle={() => setOpenTool(t => t === 'harakat' ? null : 'harakat')}>
                  <DbHarakatTrainer key={verseIdx} verseWords={verseWords} />
                </StudyTool>

                <StudyTool icon="✏️" name="Cloze Exercise" meta="Fill the gaps"
                  isOpen={openTool === 'cloze'} onToggle={() => setOpenTool(t => t === 'cloze' ? null : 'cloze')}>
                  <DbClozeExercise key={verseIdx} verseWords={verseWords} analysis={analysis} />
                </StudyTool>

                <StudyTool icon="🎯" name="Drills" meta="8 exercises — root · pattern · POS · match · ṣadr/ʿajz · synonym"
                  isOpen={openTool === 'drills'} onToggle={() => setOpenTool(t => t === 'drills' ? null : 'drills')}>
                  <DbDrillsPanel key={verseIdx} analysis={analysis} verseWords={verseWords} displayVerses={displayVerses} verseIdx={verseIdx} />
                </StudyTool>
              </div>
            )}

            {/* Navigation */}
            <div ref={navRef} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.75rem' }}>
              <button className="bayt-arrow" onClick={() => changeVerse(-1)} disabled={verseIdx === 0}>←</button>
              <VerseDepthStrip
                total={displayVerses.length}
                depths={depths}
                current={verseIdx}
                onNavigate={i => { setVerseIdx(i); setOpenTool(null); }}
              />
              <button className="bayt-arrow" onClick={() => changeVerse(1)} disabled={verseIdx === displayVerses.length - 1}>→</button>
            </div>
          </div>
        )}

        {/* ── REFERENCE TAB ── */}
        {activeTab === 'Reference' && (
          <div ref={refSectionRef} style={{ padding: '1.5rem', position: 'relative', zIndex: 1 }}>

            {/* Saved words for this poem */}
            <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: '0.75rem' }}>
              Saved Words · {savedWords.length}
            </div>

            {savedWords.length === 0 ? (
              <div style={{ fontSize: '0.82rem', color: 'var(--text3)', fontStyle: 'italic', marginBottom: '1.25rem', lineHeight: 1.6 }}>
                No words here yet — tap any highlighted word to save it.
              </div>
            ) : (
              <div style={{ marginBottom: '1.25rem' }}>
                {savedWords.map(w => (
                  <div key={w.id} style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', padding: '0.55rem 0', borderBottom: '1px solid var(--border2)' }}>
                    <span style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '1.05rem', color: 'var(--text)', direction: 'rtl', minWidth: '4rem' }}>{w.wordAr}</span>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text2)', flexShrink: 0 }}>{w.romanisation}</span>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text3)', marginLeft: 'auto', textAlign: 'right' }}>{w.meaningEn}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginBottom: '1.5rem' }}>
              <button
                onClick={() => navigate('/words', { state: { tab: 'flash' } })}
                style={{
                  width: '100%', padding: '0.7rem',
                  background: 'var(--em-glow)', border: '1px solid var(--border)',
                  borderRadius: 4, fontFamily: "'Josefin Sans', sans-serif",
                  fontSize: '0.65rem', letterSpacing: '0.12em',
                  textTransform: 'uppercase', color: 'var(--em)', cursor: 'pointer',
                }}>
                View in Flashcards →
              </button>
            </div>

            <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: '0.75rem' }}>
              Poem Details
            </div>
            <div style={{ background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 4, padding: '0.85rem 1rem', marginBottom: '1rem' }}>
              {poem.poetAr && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: '1px solid var(--border2)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text3)', fontFamily: "'Josefin Sans', sans-serif", letterSpacing: '0.06em', textTransform: 'uppercase' }}>Poet</span>
                <span style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.95rem', color: 'var(--text2)', direction: 'rtl' }}>{poem.poetAr}</span>
              </div>}
              {poem.era && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: '1px solid var(--border2)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text3)', fontFamily: "'Josefin Sans', sans-serif", letterSpacing: '0.06em', textTransform: 'uppercase' }}>Era</span>
                <span style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.9rem', color: 'var(--text2)', direction: 'rtl' }}>{poem.era}</span>
              </div>}
              {poem.meter && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: '1px solid var(--border2)' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text3)', fontFamily: "'Josefin Sans', sans-serif", letterSpacing: '0.06em', textTransform: 'uppercase' }}>Metre</span>
                <span style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.9rem', color: 'var(--text2)', direction: 'rtl' }}>{poem.meter}</span>
              </div>}
              {poem.topic && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text3)', fontFamily: "'Josefin Sans', sans-serif", letterSpacing: '0.06em', textTransform: 'uppercase' }}>Topic</span>
                <span style={{ fontFamily: "'Noto Naskh Arabic', serif", fontSize: '0.9rem', color: 'var(--text2)', direction: 'rtl' }}>{poem.topic}</span>
              </div>}
            </div>
            <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '0.58rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: '0.5rem' }}>
              {verses.length} Bayts · {analysis?.vocabulary?.length ? `${analysis.vocabulary.length} words analysed` : 'Go to Study tab to analyse words'}
            </div>
          </div>
        )}

        <BugReportLink
          screen={activeTab === 'Study' ? 'Poem / Study' : 'Poem / Read'}
          context={{ poemId, poemTitleAr: poem?.titleAr, poetAr: poem?.poetAr, verseIndex: verseIdx + 1 }}
          returnTo={{ pathname: `/poem/${poemId}`, state: { resumeVerse: verseIdx } }}
        />
      </div>
      <BottomNav />
      {helpOpen && (
        <HelpSheet
          title={activeTab === 'Study' ? 'Study tab guide' : activeTab === 'Reference' ? 'Reference tab guide' : 'Read tab guide'}
          items={activeTab === 'Study' ? STUDY_HELP : activeTab === 'Reference' ? REFERENCE_HELP : READ_HELP}
          onClose={() => setHelpOpen(false)}
        />
      )}
      {spotlightOpen && (
        <SpotlightTour
          steps={
            activeTab === 'Study' ? [
              { ref: memoryRowRef,    title: 'My Progress', desc: 'These tags track your learning journey with a poem — move from Studying to Memorising as your recall builds, then Memorised once you can recite it. Tap ♥ Favourite for poems you love. All tagged poems are saved in the Saved screen (the bookmark icon at the bottom), where you can filter by status.' },
              { ref: tabBarRef,       title: 'Study Tab',      desc: 'Seven tools per bayt, unlocked once a verse is analysed. Each analysis is cached permanently — the first reader pays a few pence, everyone after gets it free.' },
              { ref: studySectionRef, title: 'Study Tools',    desc: 'Plain Reading, Literary Devices, Vocabulary, Root Explorer, Grammar Layer (colour-coded إعراب), Harakat Trainer, and a Cloze fill-the-gaps exercise. Tap any row to expand.' },
              { ref: navRef,          title: 'Navigate Bayts', desc: 'Use the arrows to move between verses, or tap any dot to jump directly to a specific bayt.' },
            ] : activeTab === 'Reference' ? [
              { ref: memoryRowRef,  title: 'My Progress', desc: 'These tags track your learning journey with a poem — move from Studying to Memorising as your recall builds, then Memorised once you can recite it. Tap ♥ Favourite for poems you love. All tagged poems are saved in the Saved screen (the bookmark icon at the bottom), where you can filter by status.' },
              { ref: tabBarRef,     title: 'Reference Tab',  desc: 'The full poem in one place — all bayts without navigation — plus every word you have saved from this poem.' },
              { ref: refSectionRef, title: 'Saved Words',    desc: 'Every word you saved while studying this poem appears here. Tap "View in Flashcards" to open a spaced-repetition review session, or go to the Words tab at the bottom to see your full deck.' },
            ] : [
              { ref: memoryRowRef, title: 'My Progress', desc: 'These tags track your learning journey with a poem — move from Studying to Memorising as your recall builds, then Memorised once you can recite it. Tap ♥ Favourite for poems you love. All tagged poems are saved in the Saved screen (the bookmark icon at the bottom), where you can filter by status.' },
              { ref: tabBarRef,    title: 'Three Modes',     desc: 'READ follows the verse word by word. STUDY unlocks seven analysis tools per bayt. REFERENCE shows the full poem and your saved words.' },
              { ref: ctxRef,       title: 'Context & Interpretation', desc: 'Tap to unlock the poem\'s historical context, literary interpretations, and critical reception. Switch between AR and EN, then adjust the reading size to your comfort.' },
              { ref: verseAreaRef, title: 'Tap Any Word',    desc: 'Tap any Arabic word to see its definition, root letters, and grammar role. Hit ＋ Save Word to add it to your flashcard deck — find all saved words in the Reference tab or the Words screen.' },
              { ref: navRef,       title: 'Navigate Bayts',  desc: 'Use the arrows to move between verses, or tap any dot to jump directly to a specific bayt.' },
              { ref: askAiRef,     title: 'Ask AI',          desc: 'Build a structured analysis prompt for any AI — choose your language, pick a provider, and open with one tap. Paste the summary back to build running memory across all verses.' },
            ]
          }
          onDone={() => setSpotlightOpen(false)}
        />
      )}
      {showAnalysisModal && verse && (
        <AnalysisModal
          poemId={poemId}
          baytId={verse.id}
          verseAr={verse.textAr}
          poemTitleAr={poem?.titleAr}
          poetAr={poem?.poetAr}
          era={poem?.era}
          verses={displayVerses}
          onClose={() => setShowAnalysisModal(false)}
          onTopUp={() => { setShowAnalysisModal(false); setShowTopUpFromPoem(true); }}
          onResult={(result) => {
            setVerseAnalyses(prev => ({ ...prev, [verseIdx]: result }));
            setAnalysisFailed(false);
            setShowAnalysisModal(false);
          }}
        />
      )}
      {showTopUpFromPoem && (
        <TopUpSheet
          userId={userId}
          currentBalance={0}
          onClose={() => setShowTopUpFromPoem(false)}
          onSuccess={() => { setShowTopUpFromPoem(false); setNoBalance(false); setShowAnalysisModal(true); }}
        />
      )}
    </div>
  );
}

// ── Thin router: DB poem → if no ID, redirect to All Paths ──────────────────
export default function Poem() {
  const { poemId: paramId } = useParams();
  const location = useLocation();
  // URL param is primary (survives refresh + deep links); state is fallback for old history entries
  const poemId = paramId || location.state?.poemId;
  if (!poemId) return <Navigate to="/all-paths" replace />;
  return <DbPoem poemId={poemId} />;
}
